<!-- app-subdomain-creators/src/Components/NewsfeedCreatorPost.vue -->

<template>
  
  <div
    ref="postCardRootEl"
    class="relative w-full bg-[#fff]"
    :class="props.sidebarEmbed ? 'mt-0 rounded-none border-0 shadow-none' : 'mt-4 rounded-xl border border-zinc-200 shadow-sm'"
  >
    <!-- Header: avatar, name, time, menu -->
    <div class="px-4 pt-3 pb-2 flex items-start justify-between">
      <div class="flex items-start gap-3">
        <img
          :src="authorAvatarSrc"
          alt="Author avatar"
          class="h-10 w-10 rounded-full object-cover"
          @error="onAvatarLoadError"
        />

        <div>
          <p class="text-sm font-semibold text-zinc-900 -mb-1">
            {{ post.authorName }}
          </p>

          <!-- Time + pretty tooltip -->
          <div class="relative group/time inline-block">
            <p class="text-xs font-medium text-zinc-500 cursor-pointer hover:underline">
              {{ displayTime }}
            </p>

            <!-- Pretty tooltip with full datetime -->
            <p
              class="pointer-events-none absolute hidden group-hover/time:block px-2 py-4 text-xs rounded-2xl
                     bg-black/70 text-white whitespace-normal z-50 top-7 left-0 shadow-lg w-64 text-center"
            >
              {{ fullTimeTooltip }}
            </p>
          </div>
          
        </div>
      </div>

       <!-- Post menu + saved bookmark + pinned indicator -->
      <div class="absolute top-[22px] right-[65px] flex items-center gap-1">
        <!-- Pinned indicator -->
        <div
          v-if="isPostPinned"
          class="relative group"
        >
          <img
            :src="pinPlatformaIcon"
            alt="Pinned post"
            class="w-6 text-[#2D8CFF]"
          />

          <p
            class="pointer-events-none absolute px-2 py-1.5 text-[11px] rounded-xl
                  bg-black/70 text-white whitespace-nowrap z-50 top-7 left-1/2
                  -translate-x-1/2 shadow-lg
                  opacity-0 translate-y-1
                  group-hover:opacity-100 group-hover:translate-y-0
                  transition-all duration-150"
          >
            Pinned Post
          </p>
        </div>

        <!-- Orange bookmark + tooltip when this post is saved by the current user -->
        <div
          v-if="isPostSaved"
          class="relative group"
        >
          <BookmarkIcon
            class="w-6 text-[#ffae00]"
          />

          <!-- Pretty tooltip for saved bookmark status -->
          <p
            class="pointer-events-none absolute px-2 py-1.5 text-[11px] rounded-xl
                  bg-black/70 text-white whitespace-nowrap z-50 top-7 left-1/2
                  -translate-x-1/2 shadow-lg
                  opacity-0 translate-y-1
                  group-hover:opacity-100 group-hover:translate-y-0
                  transition-all duration-150"
          >
            Saved Post
          </p>
        </div>
      </div>


        <!-- Post menu -->
        <NewsfeedCreatorPostMenu
          :post-id="post.id"
          :is-saved="isPostSaved"
          :is-pinned="isPostPinned"
          :in-saved-tab="isSavedContext"
          @save-post="handleSavePost"
          @unsave-post="handleUnsavePost"
          @copy-post-link="handleCopyPostLink"
          @pin-post="handleTogglePinned"
          @edit-post="(payload) => emit('edit-post', payload)"
          @delete-post="forwardDeletePost"
          @toggle-commenting="(payload) => emit('toggle-commenting', payload)"
        />


    </div>

    <!-- Body text (truncated with inline "See more") -->
    <div
      v-if="rawBody"
      :class="hasTextBackground ? 'px-0 pb-3' : 'px-4 pb-1'"
    >
      <div
        :class="hasTextBackground ? ' min-h-[472px] flex items-center justify-center text-center px-8 py-6 bg-cover bg-center bg-no-repeat' : ''"
        :style="textBackgroundStyle"
      >
        <p
          :class="[bodyTextClass, 'leading-snug whitespace-pre-line break-words', hasTextBackground ? backgroundTextToneClass : 'text-zinc-800']"
        >
          <span v-html="displayBodyHtml"></span>
          <button
            v-if="isBodyLong && !hasTextBackground"
            type="button"
            :class="hasTextBackground ? `inline text-sm font-semibold ml-1 hover:underline ${backgroundTextButtonClass}` : 'inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline'"
            @click.stop="showFullBody = !showFullBody"
          >
            {{ showFullBody ? 'Show less' : 'See more' }}
          </button>
        </p>
      </div>
    </div>


    <NewsfeedPostPollCard
      v-if="hasPoll"
      :post-id="post.id"
      :post="post"
      :api-proxy-url="apiProxyUrl"
      :can-manage="canManagePoll"
    />

    <!-- 🔹 Media (images/videos) -->
    <div v-if="hasMedia && !hasPoll && !props.sidebarEmbed" class="">
      <!-- Single video -->
      <div
        v-if="displayMediaItems.length === 1 && displayMediaItems[0].type === 'video'"
        class="w-full bg-black flex items-center justify-center overflow-hidden"
      >
        <div class="w-full">
          <NewsfeedInlineVideoPlayer
            :file-id="displayMediaItems[0].fileId ? String(displayMediaItems[0].fileId) : null"
            :feature-instance-id="resolvedMediaFeatureInstanceId"
            :src="displayMediaItems[0].src"
            :poster="displayMediaItems[0].thumbnailSrc || displayMediaItems[0].poster || null"
            :autoplay="true"
            :muted="true"
            :max-height="680"
            @open-viewer="openViewerAt(0)"
            @loaded="emit('media-loaded', { postId: post.id })"
          />
        </div>
      </div>

      <!-- Single image -->
      <div
        @click="handleImageActivation(displayMediaItems[0], 0)"
        v-else-if="displayMediaItems.length === 1"
        class="w-full bg-black flex items-center justify-center overflow-hidden cursor-pointer"
      >
        <img
          :src="displayMediaItems[0].src"
          alt="Post media"
          class="max-h-[60vh] max-w-full w-auto object-contain"
          @load="emit('media-loaded', { postId: post.id })"
          @error="emit('media-loaded', { postId: post.id })"
        />
      </div>

      <!-- Multiple images: grid with white gutters + +N overlay (like modal) -->
      <div
        v-else
        class="w-full bg-black flex items-center justify-center overflow-hidden"
      >
        <!-- inner container: no outer padding; gutters only between tiles -->
        <div class="w-full max-h-[60vh] overflow-hidden">
          <div
            class="grid w-full gap-[3px] bg-white"
            :class="displayGridClass"
          >
            <div
              v-for="(item, idx) in displayMediaItems"
              :key="idx"
              :class="['bg-black cursor-pointer', displayTileClass(idx)]"
              @click="handleMediaTileClick(item, idx)"
            >
              <NewsfeedInlineVideoPlayer
                v-if="item.type === 'video' && isInlinePlayingVideoTile(idx)"
                :file-id="item.fileId ? String(item.fileId) : null"
                :feature-instance-id="resolvedMediaFeatureInstanceId"
                :src="item.src"
                :poster="item.thumbnailSrc || item.poster || null"
                :autoplay="true"
                :muted="true"
                :max-height="680"
                :fill-container="true"
                @open-viewer="openViewerAt(idx)"
                @loaded="emit('media-loaded', { postId: post.id })"
              />

              <img
                v-else
                :src="item.type === 'video' ? (item.thumbnailSrc || item.src) : item.src"
                alt="Post media"
                :class="['w-full h-full', multiImageFitClass]"
                @load="emit('media-loaded', { postId: post.id })"
                @error="emit('media-loaded', { postId: post.id })"
              />

              <div
                v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div class="rounded-full bg-black/50 p-2 border-[3px] border-white">
                  <PlayIcon class="w-12 h-12 text-white" />
                </div>
              </div>

              <button
                v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                type="button"
                class="absolute inset-0 z-10"
                @click.stop="playInlineVideoAt(idx)"
                aria-label="Play video"
              ></button>


              <!-- +N overlay on last tile if there are more than 5 -->
              <div
                @click.stop="openViewerAt(idx)"
                v-if="idx === displayMediaItems.length - 1 && extraMediaCount > 0"
                class="absolute inset-0 bg-black/45 flex items-center justify-center cursor-pointer"
              >
                <span class="text-white text-2xl font-semibold">
                  +{{ extraMediaCount }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>



    </div>


    <!-- Reactions summary -->
    <div
      v-if="hasEngagement"
      class="flex items-center text-xs text-zinc-500 mx-4 py-2 "
      :class="hasReactions && hasComments ? 'justify-between' : 'justify-start'"
    >
      <!-- LEFT: reaction icons + total + per-icon tooltip -->
      <div
        v-if="hasReactions"
        class="relative flex items-center gap-2 "
      >
        <!-- stacked icons -->
        <div class="flex items-center -space-x-0.5">
          <div
            v-for="(type, index) in summaryTypesToShow"
            :key="type"
            class="relative group cursor-pointer"
            @mouseenter="(e) => handleReactionTooltipEnter(type, e)"
            :style="{ zIndex: summaryTypesToShow.length - index }"
          >
            <div
              class="flex items-center justify-center rounded-full h-5 w-5 ring-2 ring-white"
            >
              <img
                :src="REACTION_META[type].icon"
                :alt="REACTION_META[type].label"
                class="h-4.5 w-4.5"
              />
            </div>

            <!-- Tooltip per reaction type, auto-flips above/below -->
            <div
              v-if="tooltipNamesForType(type).length || tooltipFallbackText(type)"
              class="pointer-events-none absolute z-40 px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg w-40
                    opacity-0 group-hover:opacity-100 transition-all duration-150"
              :class="reactionTooltipPosition[type] === 'above'
                ? 'bottom-full mb-2'
                : 'top-full mt-2'"
              style="left: 0;"
            >
              <span class="block font-semibold mb-1">
                {{ REACTION_META[type].label }}
              </span>

              <!-- Names stacked, one per line -->
              <div class="leading-tight">
                <div
                  v-for="name in tooltipNamesForType(type)"
                  :key="name"
                  class="truncate"
                >
                  {{ name }}
                </div>

                <!-- Fallback text if we have counts but no names -->
                <div
                  v-if="!tooltipNamesForType(type).length && tooltipFallbackText(type)"
                >
                  {{ tooltipFallbackText(type) }}
                </div>

                <!-- Optional '+N more' line -->
                <div
                  v-if="tooltipExtraCountForType(type) > 0"
                  class="mt-1 text-[11px] text-zinc-300"
                >
                  +{{ tooltipExtraCountForType(type) }} more
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- total reactions -->
        <span class="text-sm text-zinc-500">
          {{ totalReactions }}
        </span>
      </div>

      <!-- RIGHT: comments count -->
      <div
        v-if="hasComments"
        class="flex items-center gap-4 cursor-pointer hover:underline"
        :class="!hasReactions ? 'ml-auto' : ''"
        @click.stop="openFullPost('view-more-comments')"
      >
        <span class="text-[15px] text-zinc-500">
          {{ localCommentsCount }} comments
        </span>
      </div>
    </div>


    <!-- Action bar: Like / Comment / Send -->
<div
  class="pb-1 pt-1 mx-3"
  :class="{
    'border-t border-zinc-200': hasEngagement
  }"
>
  <div class="flex items-center justify-between">
    <!-- LIKE + hover reaction picker -->
    <div
      class="relative flex-1 flex justify-center"
      @mouseenter="handleReactionMouseEnter"
      @mouseleave="handleReactionMouseLeave"
    >
      <button
        type="button"
        class="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
               hover:bg-zinc-100 text-sm font-medium w-full"
        @click="handleLikeButtonClick"
      >
        <span
          :class="[
            'newsfeed-like-sprite',
            hasUserLiked ? 'newsfeed-like-sprite--active' : ''
          ]"
        ></span>

        <span
          :class="[
            'text-base',
            hasUserLiked ? 'text-[#2D8CFF]' : 'text-zinc-500'
          ]"
        >
          Like
        </span>
      </button>

      <!-- Hover popover (desktop-style) -->
      <NewsfeedReactionPicker
        v-if="isReactionDockOpen"
        @select="handleReactionSelect"
      />
    </div>

    <!-- COMMENT -->
    <button
    type="button"
    class="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
          hover:bg-zinc-100 text-sm text-zinc-600 font-medium flex-1"
    @click.stop="openFullPost('comment-button')"
  >
    <span class="newsfeed-comment-sprite opacity-50"></span>
    <span class="text-zinc-500 text-base">Comment</span>
  </button>


    <!-- SEND -->
    <button
      type="button"
      class="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
             hover:bg-zinc-100 text-sm text-zinc-600 font-medium flex-1"
    >
      <span class="newsfeed-send-sprite opacity-50"></span>
      <span class="text-zinc-500 text-base">Send</span>
    </button>
  </div>
</div>

<!-- Comments list (MVP) -->
<div
  v-if="comments && comments.length"
  class="border-t border-zinc-200 pt-2 pb-1 mx-3"
>
  <p
  v-if="(Number(localCommentsCount) || 0) > 1"
  @click.stop="openFullPost('view-more-comments')"
  class="px-1 text-sm font-medium text-zinc-500 cursor-pointer"
>
  View more comments
</p>


 <div
  v-for="c in visibleComments"
  :key="c.id"
  class="relative flex items-start gap-2 py-2 nf-comment-fade-in"
  :ref="(el) => setCommentRowRef(c.id, el)"
>


  <!-- thread line (UI only, like FB) -->
<div
  v-if="SHOW_COMMENT_REPLIES_UI && (getCommentReplyCount(c) > 0 || latestReplyByParentId[c.id] || openReplyForCommentId === c.id)"
  class="absolute left-4 top-[44px] w-6 pointer-events-none
         border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
  :style="{ bottom: threadLineBottomStyle(c.id) }"
></div>



    <img
      :src="resolveAvatarFromEntity(c, defaultAuthorAvatar)"
      alt="Comment avatar"
      class="h-8 w-8 rounded-full object-cover"
      @error="onAvatarLoadError"
    />

    <div class="flex-1 min-w-0">
  <!-- ✅ Keep shrink-fit ONLY for bubble + meta row -->
  <div :class="isEditingItem('comment', c.id) ? 'block w-full max-w-full align-top' : 'inline-block max-w-full align-top'">
    <!-- Bubble -->
    <div :class="isEditingItem('comment', c.id) ? 'relative bg-[#f0f3f8] rounded-2xl px-3 py-2 w-full max-w-full group/commentRow' : 'relative inline-block w-fit bg-[#f0f3f8] rounded-2xl px-3 py-2 max-w-[calc(100%-0.75rem)] min-w-44 group/commentRow'">
      <div
        v-if="!isEditingItem('comment', c.id)"
        class="text-[13px] font-semibold text-zinc-900 leading-tight"
      >
        {{ c.authorName || 'User' }}
      </div>

      <div class="text-sm text-zinc-800 whitespace-pre-line break-words leading-snug">
        <div
          v-if="!isEditingItem('comment', c.id)"
          class="absolute -right-10 top-1 opacity-0 group-hover/commentRow:opacity-100 transition-opacity duration-150"
        >
          <NewsfeedCreatorCommentReplyMenu
            :target-id="c.id"
            target-type="comment"
            @edit="handleCommentReplyMenuEdit"
            @delete="handleCommentReplyMenuDelete"
          />
        </div>

        <template v-if="isEditingItem('comment', c.id)">
          <div class="mt-1">
            <textarea
              :ref="setActiveEditTextareaRef"
              v-model="editDraftBody"
              rows="1"
              class="inline-edit-textarea w-full bg-transparent resize-none overflow-y-hidden border-none outline-none text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
              @input="autoResizeInlineTextarea"
              @keydown="onEditTextareaKeydown"
            ></textarea>

            <div v-if="inlineEditMediaPreviewItem" class="mt-2">
              <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                <img
                  v-if="inlineEditMediaPreviewItem.type === 'image'"
                  :src="inlineEditMediaPreviewItem.src"
                  alt="Selected edit media"
                  class="max-h-24 max-w-[180px] object-cover"
                />
                <video
                  v-else
                  :src="inlineEditMediaPreviewItem.src"
                  class="max-h-24 max-w-[180px] object-cover"
                  controls
                  muted
                ></video>
                <button
                  type="button"
                  class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                  @mousedown.prevent
                  @click.stop="clearInlineEditMediaSelection"
                  aria-label="Remove media"
                >
                  <XMarkIcon class="w-4 h-4 text-zinc-700" />
                </button>
              </div>
            </div>

            <input
              :ref="setInlineEditMediaInputRef"
              type="file"
              class="hidden"
              accept="image/*,video/*"
              @change="handleInlineEditMediaPicked"
            />

            <div class="mt-2 flex items-center justify-between gap-3">
              <p v-if="editErrorMessage" class="text-xs text-red-600 font-medium">
                {{ editErrorMessage }}
              </p>
              <p v-else class="text-xs text-zinc-500">
                Press Esc to cancel
              </p>

              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="rounded-full p-1 hover:bg-zinc-200"
                  :disabled="isSubmittingEdit"
                  @mousedown.prevent
                  @click.stop="openInlineEditMediaPicker"
                  aria-label="Attach media"
                >
                  <CameraIcon class="w-5 h-5 text-zinc-500" />
                </button>
                <button
                  v-if="inlineEditMediaRemoved && !pendingInlineEditMedia"
                  type="button"
                  class="text-xs font-semibold text-zinc-600 hover:underline"
                  :disabled="isSubmittingEdit"
                  @click.stop="undoInlineEditMediaRemoval"
                >
                  Undo media remove
                </button>
                <button
                  type="button"
                  class="text-xs font-semibold text-zinc-600 hover:underline"
                  :disabled="isSubmittingEdit"
                  @click.stop="cancelInlineEdit"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="text-xs font-semibold text-[#2D8CFF] hover:underline disabled:text-zinc-300"
                  :disabled="isSubmittingEdit || !canSubmitInlineEdit"
                  @click.stop="submitInlineEdit"
                >
                  {{ isSubmittingEdit ? 'Saving...' : 'Save' }}
                </button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          {{ getCommentDisplayBody(c) }}

          <button
            v-if="isCommentLong(c)"
            type="button"
            :class="[
              'inline text-xs font-semibold ml-1 hover:underline',
              isCommentExpanded(c.id)
                ? 'text-zinc-400 hover:text-zinc-500'
                : 'text-zinc-700 hover:text-zinc-800'
            ]"
            @click.stop="toggleCommentExpanded(c.id)"
          >
            {{ isCommentExpanded(c.id) ? 'Show less' : 'See more' }}
          </button>

          <div v-if="getCommentMediaItems(c).length" class="mt-2 space-y-2">
            <div
              v-for="(item, itemIndex) in getCommentMediaItems(c)"
              :key="`comment-${c.id}-media-${itemIndex}`"
              class="overflow-hidden rounded-xl bg-black/5 nf-media-shell"
              :class="{ 'nf-media-shell--loading': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
              :ref="() => ensureCommentMediaSkeletonFallback(getCommentMediaKey(c.id, itemIndex, item))"
            >
              <NewsfeedInlineVideoPlayer
                v-if="item.type === 'video'"
                :file-id="item.fileId ? String(item.fileId) : null"
                :feature-instance-id="resolvedMediaFeatureInstanceId"
                :src="item.src"
                :poster="item.poster || null"
                :autoplay="false"
                :muted="false"
                :max-height="460"
                class="nf-comment-video-frame"
                :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
                @loaded="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                @open-viewer="openViewerWithItems(getCommentMediaItems(c), itemIndex)"
              />
              <img
                v-else
                :src="item.src"
                alt="Comment media"
                class="max-h-60 w-auto max-w-full object-contain transition-opacity duration-300"
                :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
                @load="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                @error="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                @click.stop="openViewerWithItems(getCommentMediaItems(c), itemIndex)"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Row under bubble: same width as bubble -->
    <div class="relative flex items-center justify-between w-full mt-[2px] px-1">
      <!-- left group -->
      <div class="relative flex items-center gap-3 pl-1">

        <div class="relative group/commentTime">
          <div class="text-xs text-zinc-500 cursor-pointer font-semibold hover:underline mt-[2px]">
            {{ formatCommentTime(c.createdAt) }}
          </div>

          <div
            class="pointer-events-none absolute hidden group-hover/commentTime:block
                  px-3 py-2 text-xs rounded-xl bg-black/70 text-white whitespace-nowrap
                  z-50 top-5 left-0 shadow-lg"
          >
            {{ formatCommentFullTooltip(c.createdAt) }}
          </div>
        </div>

        <!-- Like -->
        <div
          class="relative"
          @mouseenter="handleCommentReactionMouseEnter(c.id)"
          @mouseleave="handleCommentReactionMouseLeave(c.id)"
        >
          <button
            type="button"
            class="text-xs font-semibold hover:underline"
            :class="getCommentReactionType(c.id) ? 'text-[#2D8CFF]' : 'text-zinc-600'"
          >
            Like
          </button>

          <NewsfeedReactionPicker
            v-if="openCommentReactionForId === c.id"
            :position-class="'absolute bottom-full left-10'"
            @select="(type) => handleCommentReactionSelect(c.id, type)"
          />
        </div>

        <!-- Reply -->
        <button
          type="button"
          class="text-xs text-zinc-600 cursor-pointer font-semibold hover:underline mt-[2px]"
          @click.stop="openReplyBox(c.id)"
        >
          Reply
        </button>

      </div>

      <!-- right badge -->
      <div
        v-if="getCommentReactionCount(c.id) > 0"
        class="relative group flex items-center gap-1"
        @mouseenter="handleCommentReactionTooltipEnter(c.id, $event)"
      >
        <span
          v-if="getCommentReactionCount(c.id) > 1"
          class="text-[11px] font-semibold text-zinc-600 leading-none"
        >
          {{ getCommentReactionCount(c.id) }}
        </span>

        <div class="flex items-center -space-x-1">
          <img
            v-for="(type, index) in getCommentTopReactionTypes(c.id)"
            :key="`${c.id}-comment-reaction-${type}`"
            :src="REACTION_META[type]?.icon"
            :alt="REACTION_META[type]?.label || 'Reaction'"
            class="h-[18px] w-[18px] rounded-full ring-2 ring-white cursor-pointer"
            :style="{ zIndex: getCommentTopReactionTypes(c.id).length - index }"
          />
        </div>

        <div
          v-if="getCommentReactionTooltipRows(c.id).length || getCommentReactionTooltipText(c.id)"
          class="pointer-events-none absolute z-50 right-0
                px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg
                opacity-0 translate-y-1
                group-hover:opacity-100 group-hover:translate-y-0
                transition-all duration-150
                whitespace-nowrap"
          :class="commentReactionTooltipPosition[String(c.id)] === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'"
        >
          <div class="">
            <template v-if="getCommentReactionTooltipRows(c.id).length > 1">
              <div
                v-for="row in getCommentReactionTooltipRows(c.id)"
                :key="`${c.id}-comment-tooltip-${row.type}`"
                class="flex items-center gap-1 mb-1 last:mb-0"
              >
                <img
                  :src="REACTION_META[row.type]?.icon"
                  :alt="REACTION_META[row.type]?.label || 'Reaction'"
                  class="h-4 w-4"
                />
                <span class="font-semibold">{{ row.count }}</span>
              </div>
            </template>
            <template v-else>
              <div class="flex items-center gap-1 mb-2">
              <img
                :src="REACTION_META[getCommentTopReactionTypes(c.id)[0] || 'like']?.icon"
                :alt="REACTION_META[getCommentTopReactionTypes(c.id)[0] || 'like']?.label || 'Reaction'"
                class="h-4 w-4"
              />
              <span class="font-semibold">
                {{ getCommentReactionCount(c.id) }}
              </span>
              </div>

              <div class="max-w-[220px] text-zinc-200 mt-1 leading-tight">
                <div
                  v-for="name in getCommentReactionTooltipNames(c.id)"
                  :key="`${c.id}-comment-name-${name}`"
                  class="truncate"
                >
                  {{ name }}
                </div>
                <div
                  v-if="getCommentReactionTooltipExtraCount(c.id) > 0"
                  class="mt-1 text-[11px] text-zinc-300"
                >
                  +{{ getCommentReactionTooltipExtraCount(c.id) }} more
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ✅ Everything below should be FULL width (NOT inside inline-block) -->

  <!-- View replies row -->
  <div
    v-if="SHOW_COMMENT_REPLIES_UI && getCommentReplyCount(c) > 0 && !hasLocalLatestReply(c.id)"
    @click.stop="openFullPost('view-reply')"
    class="mt-1 pt-2 pl-1 text-base font-medium text-zinc-500 cursor-pointer select-none hover:underline w-full"
  >
    View {{ getCommentReplyCount(c) }} {{ getCommentReplyCount(c) === 1 ? 'reply' : 'replies' }}
  </div>

  <!-- Latest reply preview (local only) -->
  <div v-if="latestReplyByParentId[c.id]" class="relative mt-1 pl-1 pt-1 w-full">
    <div class="flex items-start gap-2 w-full">
      <img
        :src="resolveAvatarFromEntity(latestReplyByParentId[c.id], defaultAuthorAvatar)"
        class="h-6 w-6 rounded-full object-cover"
        alt="Reply avatar"
        :ref="(el) => setReplyAvatarRef(c.id, el)"
        @error="onAvatarLoadError"
      />

      <div :class="isEditingItem('reply', latestReplyByParentId[c.id].id) ? 'relative bg-[#f0f3f8] rounded-2xl px-3 py-2 block w-full max-w-full group/replyRow' : 'relative inline-block w-fit bg-[#f0f3f8] rounded-2xl px-3 py-2 max-w-[calc(100%-0.75rem)] min-w-44 group/replyRow'">

        <div
          v-if="!isEditingItem('reply', latestReplyByParentId[c.id].id)"
          class="text-[13px] font-semibold text-zinc-900 leading-tight"
        >
          {{ latestReplyByParentId[c.id].authorName || 'User' }}
        </div>
        <div class="text-sm text-zinc-800 whitespace-pre-line break-words leading-snug">
          <div
            v-if="!isEditingItem('reply', latestReplyByParentId[c.id].id)"
            class="absolute -right-10 top-1 opacity-0 group-hover/replyRow:opacity-100 transition-opacity duration-150"
          >
            <NewsfeedCreatorCommentReplyMenu
              :target-id="latestReplyByParentId[c.id].id"
              target-type="reply"
              @edit="handleCommentReplyMenuEdit"
              @delete="handleCommentReplyMenuDelete"
            />
          </div>

          <template v-if="isEditingItem('reply', latestReplyByParentId[c.id].id)">
            <div class="mt-1">
              <textarea
                :ref="setActiveEditTextareaRef"
                v-model="editDraftBody"
                rows="1"
                class="inline-edit-textarea w-full bg-transparent resize-none overflow-y-hidden border-none outline-none text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                @input="autoResizeInlineTextarea"
                @keydown="onEditTextareaKeydown"
              ></textarea>

              <div v-if="inlineEditMediaPreviewItem" class="mt-2">
                <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                  <img
                    v-if="inlineEditMediaPreviewItem.type === 'image'"
                    :src="inlineEditMediaPreviewItem.src"
                    alt="Selected edit media"
                    class="max-h-24 max-w-[180px] object-cover"
                  />
                  <video
                    v-else
                    :src="inlineEditMediaPreviewItem.src"
                    class="max-h-24 max-w-[180px] object-cover"
                    controls
                    muted
                  ></video>
                  <button
                    type="button"
                    class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                    @mousedown.prevent
                    @click.stop="clearInlineEditMediaSelection"
                    aria-label="Remove media"
                  >
                    <XMarkIcon class="w-4 h-4 text-zinc-700" />
                  </button>
                </div>
              </div>

              <input
                :ref="setInlineEditMediaInputRef"
                type="file"
                class="hidden"
                accept="image/*,video/*"
                @change="handleInlineEditMediaPicked"
              />

              <div class="mt-2 flex items-center justify-between gap-3">
                <p v-if="editErrorMessage" class="text-xs text-red-600 font-medium">
                  {{ editErrorMessage }}
                </p>
                <p v-else class="text-xs text-zinc-500">
                  Press Esc to cancel
                </p>

                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="rounded-full p-1 hover:bg-zinc-200"
                    :disabled="isSubmittingEdit"
                    @mousedown.prevent
                    @click.stop="openInlineEditMediaPicker"
                    aria-label="Attach media"
                  >
                    <CameraIcon class="w-5 h-5 text-zinc-500" />
                  </button>
                  <button
                    v-if="inlineEditMediaRemoved && !pendingInlineEditMedia"
                    type="button"
                    class="text-xs font-semibold text-zinc-600 hover:underline"
                    :disabled="isSubmittingEdit"
                    @click.stop="undoInlineEditMediaRemoval"
                  >
                    Undo media remove
                  </button>
                  <button
                    type="button"
                    class="text-xs font-semibold text-zinc-600 hover:underline"
                    :disabled="isSubmittingEdit"
                    @click.stop="cancelInlineEdit"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="text-xs font-semibold text-[#2D8CFF] hover:underline disabled:text-zinc-300"
                    :disabled="isSubmittingEdit || !canSubmitInlineEdit"
                    @click.stop="submitInlineEdit"
                  >
                    {{ isSubmittingEdit ? 'Saving...' : 'Save' }}
                  </button>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            {{ latestReplyByParentId[c.id].body }}

            <div v-if="getCommentMediaItems(latestReplyByParentId[c.id]).length" class="mt-2 space-y-2">
              <div
                v-for="(item, itemIndex) in getCommentMediaItems(latestReplyByParentId[c.id])"
                :key="`latest-reply-${c.id}-media-${itemIndex}`"
                class="overflow-hidden rounded-xl bg-black/5 nf-media-shell"
                :class="{ 'nf-media-shell--loading': !isCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item)) }"
                :ref="() => ensureCommentMediaSkeletonFallback(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item))"
              >
                <NewsfeedInlineVideoPlayer
                  v-if="item.type === 'video'"
                  :file-id="item.fileId ? String(item.fileId) : null"
                  :feature-instance-id="resolvedMediaFeatureInstanceId"
                  :src="item.src"
                  :poster="item.poster || null"
                  :autoplay="false"
                  :muted="false"
                  :max-height="460"
                  class="nf-comment-video-frame"
                  :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item)) }"
                  @loaded="markCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item))"
                  @open-viewer="openViewerWithItems(getCommentMediaItems(latestReplyByParentId[c.id]), itemIndex)"
                />
                <img
                  v-else
                  :src="item.src"
                  alt="Reply media"
                  class="max-h-60 w-auto max-w-full object-contain transition-opacity duration-300"
                  :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item)) }"
                  @load="markCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item))"
                  @error="markCommentMediaLoaded(getCommentMediaKey(`latest-reply-${c.id}`, itemIndex, item))"
                  @click.stop="openViewerWithItems(getCommentMediaItems(latestReplyByParentId[c.id]), itemIndex)"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <!-- Reply box (MVP) -->
  <div v-if="openReplyForCommentId === c.id" class="mt-2 pl-1 w-full">
    <div class="flex items-start gap-2 w-full min-w-0">
      <img
        :src="commenterAvatarSrc"
        alt="Your avatar"
        class="h-6 w-6 rounded-full object-cover"
        :ref="(el) => setReplyComposerAvatarRef(c.id, el)"
        @error="onAvatarLoadError"
      />

      <div class="flex-1 bg-[#f0f3f8] rounded-2xl px-3 py-2 w-full min-w-0">
        <textarea
          v-model="replyTextByCommentId[c.id]"
          rows="1"
          class="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0
                 text-sm text-zinc-900 placeholder:text-zinc-500 resize-none overflow-y-hidden"
          :placeholder="`Reply as ${postingAsName || post.currentUserName || 'You'}`"
          @keydown.enter.exact.prevent="submitReply(c.id)"
          @keydown.enter.shift.exact.stop
        ></textarea>

        <div v-if="pendingReplyMediaByCommentId[c.id]" class="mt-2">
          <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
            <img
              v-if="pendingReplyMediaByCommentId[c.id]?.type === 'image'"
              :src="pendingReplyMediaByCommentId[c.id]?.previewUrl"
              alt="Selected reply media"
              class="max-h-24 max-w-[180px] object-cover"
            />
            <video
              v-else
              :src="pendingReplyMediaByCommentId[c.id]?.previewUrl"
              class="max-h-24 max-w-[180px] object-cover"
              controls
              muted
            ></video>
            <button
              type="button"
              class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
              @mousedown.prevent
              @click.prevent="clearPendingReplyMedia(c.id)"
              aria-label="Remove media"
            >
              <XMarkIcon class="w-4 h-4 text-zinc-700" />
            </button>
          </div>
        </div>

        <input
          :ref="(el) => setReplyMediaInputRef(c.id, el)"
          type="file"
          class="hidden"
          accept="image/*,video/*"
          @change="(e) => handleReplyMediaPicked(c.id, e)"
        />

        <div class="flex items-center justify-between mt-1">
          <div class="relative flex items-center gap-1">
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="toggleReplyEmojiPicker(c.id)"
            >
              <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
            </button>
            <div
              v-if="openReplyEmojiForCommentId === c.id"
              class="absolute bottom-full left-0 mb-2 z-40"
            >
              <EmojiPicker @select="(emoji) => addReplyEmoji(c.id, emoji)" />
            </div>
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="triggerReplyMediaPicker(c.id)"
            >
              <CameraIcon class="w-6 h-6 text-zinc-400" />
            </button>
          </div>
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-full hover:bg-zinc-200 p-[2px]"
            @click="submitReply(c.id)"
            :disabled="isSubmittingReply || !hasReplyContent(c.id)"
          >
            <PaperAirplaneIcon
              class="w-5 h-5"
              :class="hasReplyContent(c.id) && !isSubmittingReply ? 'text-[#2D8CFF]' : 'text-zinc-300'"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</div>




  </div>
  
</div>



    <!-- Quick reply row -->
    <div class="py-2 flex items-start gap-2 mx-3">
      <img
        :src="commenterAvatarSrc"
        alt="Your avatar"
        class="h-8 w-8 rounded-full object-cover"
        @error="onAvatarLoadError"
      />

      <!-- Comment bar (full width, icons inside) -->
      <div
        class="flex-1 bg-[#f0f3f8] px-3 text-sm text-zinc-500 transition-all duration-150"
        :class="isCommentActive ? 'rounded-2xl py-2 flex flex-col' : 'rounded-full h-9 flex items-center'"
      >
        <!-- Top row: textarea (+ icons when collapsed) -->
        <div
          class="flex items-center gap-2"
          :class="isCommentActive ? '' : 'flex-1'"
        >
          <textarea
            ref="commentTextarea"
            v-model="commentText"
            rows="1"
            class="flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0
                  text-sm text-zinc-900 placeholder:text-zinc-500 resize-none overflow-y-hidden
                  px-0"
            :class="isCommentActive
              ? 'py-0 leading-snug'
              : 'h-9 py-2 leading-5'"
            :placeholder="`Comment as ${postingAsName || post.currentUserName || 'You'}`"
            @focus="isCommentActive = true"
            @blur="handleCommentBlur"
            @input="autoResize"
            @keydown.enter.exact.prevent="submitComment"
            @keydown.enter.shift.exact.stop
          ></textarea>


          <!-- Icons inline on the right when collapsed -->
          <div
            v-if="!isCommentActive"
            class="relative flex items-center gap-1 text-zinc-400 text-lg ml-2"
          >
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="toggleCommentEmojiPicker"
            >
              <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
            </button>

            <div
              v-if="showCommentEmojiPicker"
              class="absolute bottom-full left-0 mb-2 z-40"
            >
              <EmojiPicker @select="addCommentEmoji" />
            </div>

            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="triggerCommentMediaPicker"
            >
              <CameraIcon class="w-6 h-6 text-zinc-400" />
            </button>
          </div>
        </div>

        <div v-if="pendingCommentMedia" class="mt-2">
          <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
            <img
              v-if="pendingCommentMedia.type === 'image'"
              :src="pendingCommentMedia.previewUrl"
              alt="Selected comment media"
              class="max-h-24 max-w-[180px] object-cover"
            />
            <video
              v-else
              :src="pendingCommentMedia.previewUrl"
              class="max-h-24 max-w-[180px] object-cover"
              controls
              muted
            ></video>
            <button
              type="button"
              class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
              @mousedown.prevent
              @click.prevent="clearPendingCommentMedia"
              aria-label="Remove media"
            >
              <XMarkIcon class="w-4 h-4 text-zinc-700" />
            </button>
          </div>
        </div>

        <input
          ref="commentMediaInput"
          type="file"
          class="hidden"
          accept="image/*,video/*"
          @change="handleCommentMediaPicked"
        />

        <!-- Icons bottom-left + send arrow right when active -->
        <div
          v-if="isCommentActive"
          class="flex items-center justify-between text-zinc-400 text-lg mt-2"
        >
          <!-- Left: emoji + camera -->
          <div class="relative flex items-center gap-1">
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="toggleCommentEmojiPicker"
            >
              <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
            </button>

            <div
              v-if="showCommentEmojiPicker"
              class="absolute bottom-full left-0 mb-2 z-40"
            >
              <EmojiPicker @select="addCommentEmoji" />
            </div>

            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
              @mousedown.prevent
              @click.prevent="triggerCommentMediaPicker"
            >
              <CameraIcon class="w-6 h-6 text-zinc-400" />
            </button>
          </div>

          <!-- Right: send button (UI only for now) -->
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-full hover:bg-zinc-200 p-[2px]"
            @click="submitComment"
            :disabled="isSubmittingComment || !hasCommentContent()"
          >
            <PaperAirplaneIcon
              class="w-5 h-5"
              :class="hasCommentContent() && !isSubmittingComment ? 'text-[#2D8CFF]' : 'text-zinc-300'"
            />
          </button>
        </div>
      </div>
    </div>


  </div>
<NewsFeedMediaPostViewer
  v-if="!props.sidebarEmbed"
  :show="viewerOpen"
  :media-items="viewerMediaItems"
  :initial-index="viewerIndex"
  :feature-instance-id="resolvedMediaFeatureInstanceId"
  :slug-component="slugSidebarComponent"
  :post="post"
  :author-avatar-src="authorAvatarSrc"
  :commenter-avatar-src="commenterAvatarSrc"
  :display-time="displayTime"
  @close="viewerOpen = false"
  @open-full-post="(payload) => emit('open-full-post', payload)"
/>


</template>

<script setup>
import {
  computed,
  defineProps,
  ref,
  defineEmits,
  watch,
  onBeforeUnmount,
  onMounted,
  nextTick,
  getCurrentInstance
} from 'vue';

import { FaceSmileIcon, CameraIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { PaperAirplaneIcon, BookmarkIcon, PlayIcon } from '@heroicons/vue/24/solid';

import NewsfeedCreatorPostMenu from '@/Components/NewsfeedCreatorPostMenu.vue';
import NewsFeedMediaPostViewer from '@/Components/NewsFeedMediaPostViewer.vue';
import NewsFeedFullPostSlug from '@/Components/NewsFeedFullPostSlug.vue';
import NewsfeedReactionPicker from '@/Components/NewsfeedReactionPicker.vue';
import NewsfeedCreatorCommentReplyMenu from '@/Components/NewsfeedCreatorCommentReplyMenu.vue';
import NewsfeedInlineVideoPlayer from '@/Components/NewsfeedInlineVideoPlayer.vue';
import NewsfeedPostPollCard from '@/Components/NewsfeedPostPollCard.vue';
import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';
import { useMediaUploadStore } from '@/stores/mediaUploadStore';
import { useWebsocketStore } from '@/stores/websocketStore';
import processingPoster from '@/assets/media/images/chat-video-thumb.gif';
import {
  NEWSFEED_BACKGROUND_MAX_CHARS,
  detectNewsfeedBackgroundTone,
  getNewsfeedBackgroundById,
  guessNewsfeedBackgroundTone,
} from '@/utils/newsfeedPostBackgrounds';

// ⬇️ BFF proxy base URL
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const app = getCurrentInstance();
const notify = app?.appContext?.config?.globalProperties?.$notify;
const slugSidebarComponent = NewsFeedFullPostSlug;
const mediaUploadStore = useMediaUploadStore();
const websocketStore = useWebsocketStore();

// reaction summary icon (Like)
const likeThumbIcon = new URL(
  '@/assets/media/images/platforma-like-thumb.svg',
  import.meta.url
).href;

const pinPlatformaIcon = new URL(
  '@/assets/media/images/pin-platforma-icon.svg',
  import.meta.url
).href;


// reaction summary icons (others)
const loveIcon  = new URL('@/assets/media/images/newsfeed-heart.svg', import.meta.url).href;
const careIcon  = new URL('@/assets/media/images/newsfeed-care.svg', import.meta.url).href;
const wowIcon   = new URL('@/assets/media/images/newsfeed-wow.svg', import.meta.url).href;
const sadIcon   = new URL('@/assets/media/images/newsfeed-sad.svg', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry.svg', import.meta.url).href;
const hahaIcon  = new URL('@/assets/media/images/newsfeed-laugh.svg', import.meta.url).href;

// Central meta map used by template
const REACTION_META = {
  like:  { label: 'Like',  icon: likeThumbIcon },
  love:  { label: 'Love',  icon: loveIcon },
  care:  { label: 'Care',  icon: careIcon },
  haha:  { label: 'Haha',  icon: hahaIcon },
  wow:   { label: 'Wow',   icon: wowIcon },
  sad:   { label: 'Sad',   icon: sadIcon },
  angry: { label: 'Angry', icon: angryIcon },
};

const REACTION_KEYS = ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry'];

const props = defineProps({
  post: { type: Object, required: true },
  postingAsName: { type: String, default: 'You' },
  postingAsAvatarUrl: { type: String, default: null },
  // tells the menu we are in the Saved tab (for the little helper text)
  isSavedContext: { type: Boolean, default: false },
  // 🔹 just to silence the "context" extraneous attribute warning
  context: { type: String, default: '' },
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  sidebarEmbed: {
    type: Boolean,
    default: false,
  },
});

const resolvedMediaFeatureInstanceId = computed(() => {
  const rawValue =
    props.featureInstanceId ??
    props.post?.featureInstanceId ??
    props.post?.instanceId ??
    props.post?.instance_id ??
    null;

  if (rawValue == null) return null;
  const normalized = String(rawValue).trim();
  return normalized ? normalized : null;
});

const buildNewsfeedImageProxyUrl = (fileId, options = {}) => {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return null;

  const params = new URLSearchParams();
  if (resolvedMediaFeatureInstanceId.value) {
    params.set('featureInstanceId', resolvedMediaFeatureInstanceId.value);
  }

  const variant = String(options?.variant || '').trim().toLowerCase();
  if (variant) {
    params.set('variant', variant);
  }

  const query = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${encodeURIComponent(normalizedFileId)}${
    query ? `?${query}` : ''
  }`;
};

const extractFileIdFromLegacyImageUrl = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return null;

  const match = value.match(
    /\/api\/(?:creator-newsfeed\/image|newsfeed\/image|image)\/([0-9a-fA-F-]{36})(?:[/?#]|$)/
  );
  return match?.[1] ? String(match[1]) : null;
};

const isKnownDefaultAvatarPlaceholder = (rawUrl) => {
  const value = String(rawUrl || '').trim().toLowerCase();
  if (!value) return false;

  return (
    value.startsWith('@/assets/') ||
    value.startsWith('src/assets/') ||
    value.startsWith('/src/assets/') ||
    value.includes('/media/images/default-avatar.png') ||
    value.endsWith('/default-avatar.png') ||
    value === 'default-avatar.png' ||
    value.includes('/src/assets/media/images/default-avatar') ||
    value.includes('/src/assets/media/images/woman-avatar')
  );
};

const normalizeAvatarUrlForCreatorProxy = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return null;
  if (isKnownDefaultAvatarPlaceholder(value)) return null;

  const legacyFileId = extractFileIdFromLegacyImageUrl(value);
  if (legacyFileId) {
    return buildNewsfeedImageProxyUrl(legacyFileId) || value;
  }

  return value;
};

const resolveAvatarFromEntity = (entity, fallback = defaultAuthorAvatar) => {
  if (!entity || typeof entity !== 'object') return fallback;

  const avatarFileId =
    entity.authorAvatarFileId ||
    entity.author_avatar_file_id ||
    entity.avatarFileId ||
    entity.avatar_file_id ||
    entity.postedAsAvatarFileId ||
    entity.posted_as_avatar_file_id ||
    null;
  if (avatarFileId) {
    const proxy = buildNewsfeedImageProxyUrl(avatarFileId);
    if (proxy) return proxy;
  }

  const raw =
    entity.authorAvatarUrl ||
    entity.author_avatar_url ||
    entity.avatarUrl ||
    entity.avatar_url ||
    entity.authorAvatar ||
    entity.author_avatar ||
    entity.postedAsAvatarUrl ||
    entity.posted_as_avatar_url ||
    null;
  const normalized = normalizeAvatarUrlForCreatorProxy(raw);
  return normalized || fallback;
};

const onAvatarLoadError = (event) => {
  const imgEl = event?.target;
  if (!(imgEl instanceof HTMLImageElement)) return;
  if (imgEl.dataset.avatarFallbackApplied === '1') return;
  imgEl.dataset.avatarFallbackApplied = '1';
  imgEl.src = defaultAuthorAvatar;
};


const emit = defineEmits([
  'save-post',
  'unsave-post',
  'pin-post',
  'edit-post',
  'delete-post',
  'toggle-commenting',
  'react',
  'pin-changed',
  'media-loaded',
  'open-full-post',
  'post-visible'
]);

const postCardRootEl = ref(null);
let postViewportObserver = null;
let postViewportVisibleTimer = null;
let hasEmittedVisibleInSession = false;

const clearPostVisibleTimer = () => {
  if (postViewportVisibleTimer) {
    clearTimeout(postViewportVisibleTimer);
    postViewportVisibleTimer = null;
  }
};

const setupPostViewportTracking = () => {
  if (props.context !== 'discussion') return;
  if (!postCardRootEl.value) return;
  if (hasEmittedVisibleInSession) return;
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

  if (postViewportObserver) {
    postViewportObserver.disconnect();
    postViewportObserver = null;
  }

  const rootEl = document.querySelector('.app-scroll') || null;

  postViewportObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries && entries[0] ? entries[0] : null;
      if (!entry) return;
      if (hasEmittedVisibleInSession) return;

      const enoughVisible = entry.isIntersecting && Number(entry.intersectionRatio || 0) >= 0.6;
      if (!enoughVisible) {
        clearPostVisibleTimer();
        return;
      }

      clearPostVisibleTimer();
      postViewportVisibleTimer = setTimeout(() => {
        if (hasEmittedVisibleInSession) return;
        hasEmittedVisibleInSession = true;
        emit('post-visible', {
          postId: props.post?.id,
          source: 'feed',
          featureInstanceId: props.featureInstanceId ?? props.post?.featureInstanceId ?? props.post?.instanceId ?? null,
        });
        if (postViewportObserver) {
          postViewportObserver.disconnect();
          postViewportObserver = null;
        }
      }, 800);
    },
    {
      root: rootEl,
      threshold: [0.6, 0.75, 0.9],
    }
  );

  postViewportObserver.observe(postCardRootEl.value);
};

// (add this helper just to log & forward)
const forwardDeletePost = (payload) => {
  console.log('[NewsfeedCreatorPost] delete-post received from menu:', payload);
  emit('delete-post', payload);
};

// 🔹 default avatar (same as header default)
const defaultAuthorAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

/**
 * Header avatar
 */
const authorAvatarSrc = computed(() => {
  const postAvatar = resolveAvatarFromEntity(props.post || {}, null);
  return postAvatar || defaultAuthorAvatar;
});

/**
 * Comment-bar avatar
 */
const commenterAvatarSrc = computed(() => {
  const postingAsAvatar = normalizeAvatarUrlForCreatorProxy(props.postingAsAvatarUrl);
  const postAvatar = resolveAvatarFromEntity(props.post || {}, null);
  return postingAsAvatar || postAvatar || defaultAuthorAvatar;
});

const currentUserDisplayName = computed(() => {
  return props.post.currentUserName || props.postingAsName || 'You';
});

const hasPoll = computed(() => {
  return !!(
    props.post?.poll ||
    props.post?.pollData ||
    props.post?.postPoll ||
    props.post?.postType === 'poll' ||
    props.post?.post_type === 'poll' ||
    Array.isArray(props.post?.pollOptions)
  );
});

const canManagePoll = computed(() => {
  const currentId = props.post?.currentUserId || props.post?.current_user_id || null;
  const currentType = props.post?.currentUserType || props.post?.current_user_type || null;
  const ownerId = props.post?.authorUserId || props.post?.author_user_id || null;
  const ownerType = props.post?.authorUserType || props.post?.author_user_type || null;

  if (currentId != null && ownerId != null) {
    if (String(currentId) !== String(ownerId)) return false;
    if (currentType != null && ownerType != null) {
      return String(currentType) === String(ownerType);
    }
    return true;
  }

  const me = String(currentUserDisplayName.value || '').trim().toLowerCase();
  const author = String(props.post?.authorName || '').trim().toLowerCase();
  return !!me && !!author && me === author;
});

const isPostSaved = computed(() => {
  const p = props.post || {};
  if (typeof p.isSaved === 'boolean') return p.isSaved;
  if (typeof p.is_saved === 'boolean') return p.is_saved;
  if (typeof p.saved === 'boolean') return p.saved;
  return false;
});


const initialIsPinned = computed(() => {
  const p = props.post || {};
  if (typeof p.isPinned === 'boolean') return p.isPinned;
  if (typeof p.is_pinned === 'boolean') return p.is_pinned;
  if (typeof p.pinned === 'boolean') return p.pinned;

  if (typeof p.isPinned === 'number') return !!p.isPinned;
  if (typeof p.is_pinned === 'number') return !!p.is_pinned;

  return false;
});

const localIsPinned = ref(initialIsPinned.value);

const isPostPinned = computed(() => localIsPinned.value);

watch(initialIsPinned, (val) => {
  localIsPinned.value = val;
});

const isPinningPost = ref(false);

async function handleTogglePinned(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn('[NewsfeedCreatorPost] handleTogglePinned: no postId found');
    return;
  }

  if (isPinningPost.value) return;
  isPinningPost.value = true;

  const currentlyPinned = isPostPinned.value;
  const shouldPin = !currentlyPinned;
  const method = shouldPin ? 'POST' : 'DELETE';

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/pin`,
      {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorPost] pin-toggle backend NOT OK:',
        res.status,
        text
      );
      alert('Failed to update pin status. Please try again.');
      return;
    }

    const data = await res.json().catch(() => ({}));
    console.log('[NewsfeedCreatorPost] pin-toggle OK:', data);

    const newState =
      typeof data.isPinned === 'boolean'
        ? data.isPinned
        : typeof data.is_pinned === 'boolean'
          ? data.is_pinned
          : shouldPin;

    localIsPinned.value = newState;

    // Let parent know (for sorting, analytics, etc.)
    emit('pin-post', { postId, isPinned: newState, backend: data });
    emit('pin-changed', { postId, isPinned: newState });
  } catch (err) {
    console.error('[NewsfeedCreatorPost] pin-toggle error:', err);
    alert('Error updating pin status. Please try again.');
  } finally {
    isPinningPost.value = false;
  }
}

const openFullPost = (source = '') => {
  const postId = props.post?.id;
  if (!postId) return;

  emit('open-full-post', {
    postId,
    source,
    post: props.post, // pass full post object so parent doesn't have to refetch right now
  });
};

async function handleCopyPostLink() {
  const slug = String(props.post?.slug || props.post?.raw?.slug || '').trim();
  if (!slug) {
    if (typeof notify === 'function') {
      notify({
        title: 'Link unavailable',
        body: 'This post does not have a slug yet.',
        variant: 'error',
      });
    }
    return;
  }

  const link = `${window.location.origin}/creator/newsfeed/post/${encodeURIComponent(slug)}`;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
    } else {
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    if (typeof notify === 'function') {
      notify({
        title: 'Post link copied',
        body: link,
        variant: 'success',
      });
    }
  } catch (err) {
    console.error('[NewsfeedCreatorPost] failed to copy post link:', err);
    if (typeof notify === 'function') {
      notify({
        title: 'Copy failed',
        body: 'Could not copy post link.',
        variant: 'error',
      });
    }
  }
}

const hasLocalLatestReply = (commentId) => {
  return !!latestReplyByParentId.value?.[commentId];
};


/* ─────────────────────────────────────────────
 * Reactions: local state (typed)
 * ───────────────────────────────────────────*/

// total (kept for older hasEngagement logic)
const localReactionsCount = ref(
  typeof props.post?.reactionsSummary?.total === 'number'
    ? props.post.reactionsSummary.total
    : (typeof props.post?.reactionsCount === 'number' ? props.post.reactionsCount : 0)
);

// per-type counts
const localReactionsSummary = ref(
  props.post?.reactionsSummary && typeof props.post.reactionsSummary === 'object'
    ? { ...props.post.reactionsSummary }
    : {
        total: localReactionsCount.value,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      }
);

// per-type names for tooltips
const localReactionNamesByType = ref(
  props.post?.reactionNamesByType && typeof props.post.reactionNamesByType === 'object'
    ? JSON.parse(JSON.stringify(props.post.reactionNamesByType))
    : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] }
);

/**
 * Current user's reaction type on this post.
 * 1) Prefer explicit field from backend if present.
 * 2) Fallback: derive from reactionNamesByType using currentUserDisplayName.
 */
const currentUserReactionType = computed(() => {
  // If backend sends explicit field, use it
  if (props.post.currentUserReactionType) {
    return props.post.currentUserReactionType;
  }

  const me = currentUserDisplayName.value;
  if (!me) return null;

  const meLower = String(me).toLowerCase();
  const source = localReactionNamesByType.value || {};

  for (const t of REACTION_KEYS) {
    const arr = Array.isArray(source[t]) ? source[t] : [];
    if (arr.some((n) => String(n).toLowerCase() === meLower)) {
      return t;
    }
  }

  return null;
});

// Highlight the Like button if the user has *any* reaction on this post
const hasUserLiked = computed(() => !!currentUserReactionType.value);


// order + only types that have counts
const activeReactionTypes = computed(() => {
  const s = localReactionsSummary.value || {};
  return REACTION_KEYS
    .map((type, index) => ({
      type,
      count: Number(s[type]) || 0,
      index,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.index - b.index;
    })
    .map((row) => row.type);
});

// show up to 3 icons like FB
const summaryTypesToShow = computed(() => activeReactionTypes.value.slice(0, 3));

const totalReactions = computed(() => Number(localReactionsSummary.value?.total) || 0);

const hasReactions = computed(() => totalReactions.value > 0);
const hasComments = computed(() => (props.post.commentsCount ?? 0) > 0);

// // Tooltip text helper used by template
// const tooltipTextForType = (type) => {
//   const total = Number(localReactionsSummary.value?.[type]) || 0;
//   if (!total) return '';

//   const rawNames = Array.isArray(localReactionNamesByType.value?.[type])
//     ? localReactionNamesByType.value[type]
//     : [];

//   const names = rawNames
//     .filter((n) => typeof n === 'string' && n.trim().length > 0)
//     .map((n) => n.trim());

//   if (!names.length) {
//     return total === 1 ? '1 person reacted' : `${total} people reacted`;
//   }

//   const MAX = 20;
//   const shown = names.slice(0, MAX);
//   const remaining = total - shown.length;

//   const base = shown.join(', ');
//   return remaining > 0 ? `${base} and ${remaining} more…` : base;
// };

/* ─────────────────────────────────────────────
 * Body (unchanged)
 * ───────────────────────────────────────────*/
const rawBody = computed(() => props.post.body || props.post.content || '');
const backgroundKey = computed(() => {
  return (
    props.post?.backgroundKey ||
    props.post?.backgroundColor ||
    props.post?.background_color ||
    null
  );
});
const resolvedBackground = computed(() => getNewsfeedBackgroundById(backgroundKey.value));
const hasAnyAttachedMedia = computed(() => {
  const atts = Array.isArray(props.post?.attachments) ? props.post.attachments : [];
  const coverMediaFileId =
    props.post?.coverMediaFileId ||
    props.post?.cover_media_file_id ||
    null;
  return atts.length > 0 || !!coverMediaFileId;
});
const hasTextBackground = computed(
  () => !!resolvedBackground.value && !hasPoll.value && !hasAnyAttachedMedia.value
);
const textBackgroundStyle = computed(() => {
  if (!hasTextBackground.value) return {};
  return {
    backgroundImage: `url(${resolvedBackground.value.backgroundUrl})`,
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
});
const textBackgroundTone = ref('dark');
const backgroundTextToneClass = computed(() => {
  return textBackgroundTone.value === 'light'
    ? 'text-[#1f2937] font-semibold drop-shadow-[0_1px_8px_rgba(255,255,255,0.35)]'
    : 'text-white font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]';
});
const backgroundTextButtonClass = computed(() => {
  return textBackgroundTone.value === 'light' ? 'text-[#1f2937]' : 'text-white';
});
watch(
  () => resolvedBackground.value?.backgroundUrl || '',
  async (url) => {
    if (!url) {
      textBackgroundTone.value = 'dark';
      return;
    }
    textBackgroundTone.value = guessNewsfeedBackgroundTone(url);
    textBackgroundTone.value = await detectNewsfeedBackgroundTone(url);
  },
  { immediate: true }
);

/* ─────────────────────────────────────────────
 * Media URLs (unchanged)
 * ───────────────────────────────────────────*/
const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif'];
const videoFileExtensions = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.ogv'];

const inferAttachmentKind = (att) => {
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase().trim();
  const fileType = String(att?.fileType || att?.type || '').toLowerCase().trim();
  const url = String(att?.url || att?.secureVideoUrl || att?.videoUrl || att?.secureImageUrl || att?.imageUrl || '').toLowerCase();

  if (mime.startsWith('video/') || fileType === 'video') return 'video';
  if (mime.startsWith('image/') || fileType === 'image') return 'image';
  if (videoFileExtensions.some((ext) => url.includes(ext))) return 'video';
  if (imageFileExtensions.some((ext) => url.includes(ext))) return 'image';
  if (att?.videoUrl || att?.secureVideoUrl || att?.thumbnailUrl || att?.posterUrl) return 'video';
  return 'image';
};

const parseCommentAttachmentJson = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw];
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
  } catch {
    return [];
  }
};

const getCommentAttachmentList = (entity) => {
  if (!entity || typeof entity !== 'object') return [];

  const direct = entity.attachments ?? entity.mediaAttachments ?? entity.media ?? null;
  if (Array.isArray(direct)) return direct.filter(Boolean);
  if (direct && typeof direct === 'object') return [direct];

  const single = entity.attachment ?? null;
  if (Array.isArray(single)) return single.filter(Boolean);
  if (single && typeof single === 'object') return [single];

  const jsonSource = entity.attachment_json ?? entity.attachments_json ?? null;
  return parseCommentAttachmentJson(jsonSource).filter(Boolean);
};

const normalizeCommentAttachmentPayload = (uploadMeta) => {
  if (!uploadMeta || typeof uploadMeta !== 'object') return null;
  const fileId = String(uploadMeta.fileId || '').trim();
  if (!fileId) return null;
  return {
    fileId,
    file_id: fileId,
    type: uploadMeta.fileType || uploadMeta.type || 'image',
    fileType: uploadMeta.fileType || uploadMeta.type || 'image',
    mimeType: uploadMeta.mimeType || null,
    mime_type: uploadMeta.mimeType || null,
    url: uploadMeta.permanentUrl || null,
    fileUrl: uploadMeta.permanentUrl || null,
    thumbnailUrl: uploadMeta.thumbnailUrl || null,
    hlsPlaylistUrl: uploadMeta.hlsPlaylistUrl || null,
    hlsStatus: uploadMeta.hlsStatus || null,
  };
};

const buildCommentVideoPlaylistUrl = (fileId) => {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return '';
  const featureId = String(resolvedMediaFeatureInstanceId.value || '').trim();
  if (!featureId) return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}`;
  return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}?featureInstanceId=${encodeURIComponent(featureId)}`;
};

const getCommentMediaItems = (entity) => {
  const atts = getCommentAttachmentList(entity);
  const items = [];

  for (const att of atts) {
    const fileId = att?.fileId || att?.file_id || att?.mediaFileId || null;
    const kind = inferAttachmentKind(att);
    const imageSrc =
      buildNewsfeedImageProxyUrl(fileId) ||
      att?.imageUrl ||
      att?.secureImageUrl ||
      att?.thumbnailUrl ||
      att?.fileUrl ||
      att?.file_url ||
      att?.url ||
      null;
    const videoSrc =
      att?.hlsPlaylistUrl ||
      att?.hls_playlist_url ||
      att?.secureVideoUrl ||
      att?.videoUrl ||
      att?.fileUrl ||
      att?.file_url ||
      att?.url ||
      (fileId ? buildCommentVideoPlaylistUrl(fileId) : null);
    const poster =
      att?.thumbnailUrl ||
      att?.posterUrl ||
      buildNewsfeedImageProxyUrl(fileId, { variant: 'thumbnail' }) ||
      imageSrc ||
      null;

    if (kind === 'video') {
      if (!videoSrc) continue;
      items.push({
        type: 'video',
        fileId: fileId ? String(fileId) : null,
        src: videoSrc,
        poster,
      });
    } else if (imageSrc) {
      items.push({
        type: 'image',
        fileId: fileId ? String(fileId) : null,
        src: imageSrc,
      });
    }
  }

  const hasProcessingVideo = Boolean(entity?.mediaProcessing ?? entity?.media_processing);
  if (hasProcessingVideo && !items.some((item) => item?.type === 'video' || item?.fileId === 'processing-video')) {
    items.push({
      type: 'image',
      fileId: 'processing-video',
      src: processingPoster,
    });
  }

  return items;
};

const loadedCommentMediaKeys = ref({});
const commentMediaSkeletonFallbackTimers = new Map();

const getCommentMediaKey = (entityId, itemIndex, item) => {
  const idPart = String(entityId ?? 'comment');
  const srcPart = String(item?.fileId || item?.src || item?.poster || itemIndex || 'media');
  return `${idPart}:${srcPart}:${itemIndex}`;
};

const isCommentMediaLoaded = (mediaKey) => !!loadedCommentMediaKeys.value?.[mediaKey];

const markCommentMediaLoaded = (mediaKey) => {
  if (!mediaKey) return;
  if (loadedCommentMediaKeys.value?.[mediaKey]) {
    void nextTick(() => scheduleComputeAllThreadLineBottoms());
    return;
  }
  loadedCommentMediaKeys.value = {
    ...(loadedCommentMediaKeys.value || {}),
    [mediaKey]: true,
  };
  const timer = commentMediaSkeletonFallbackTimers.get(mediaKey);
  if (timer) {
    clearTimeout(timer);
    commentMediaSkeletonFallbackTimers.delete(mediaKey);
  }
  void nextTick(() => scheduleComputeAllThreadLineBottoms());
};

const ensureCommentMediaSkeletonFallback = (mediaKey) => {
  if (!mediaKey || isCommentMediaLoaded(mediaKey) || commentMediaSkeletonFallbackTimers.has(mediaKey)) return;
  const timer = setTimeout(() => {
    markCommentMediaLoaded(mediaKey);
    commentMediaSkeletonFallbackTimers.delete(mediaKey);
  }, 1400);
  commentMediaSkeletonFallbackTimers.set(mediaKey, timer);
};

const mediaDisplayAllItems = computed(() => {
  const items = [];
  const atts = Array.isArray(props.post.attachments) ? props.post.attachments : [];

  for (const att of atts) {
    if (!att) continue;
    const fileId = att.fileId || att.file_id || att.mediaFileId || null;
    const kind = inferAttachmentKind(att);
    const imageSrc =
      buildNewsfeedImageProxyUrl(fileId) ||
      att.imageUrl ||
      att.secureImageUrl ||
      att.url ||
      null;
    const videoSrc = att.secureVideoUrl || att.videoUrl || att.url || null;
    const thumbnailSrc =
      att.thumbnailUrl ||
      att.posterUrl ||
      buildNewsfeedImageProxyUrl(fileId, { variant: 'thumbnail' }) ||
      imageSrc;

    if (kind === 'video') {
      const resolvedVideoSrc = videoSrc || imageSrc;
      if (!resolvedVideoSrc) continue;
      items.push({
        type: 'video',
        fileId: fileId ? String(fileId) : null,
        src: resolvedVideoSrc,
        thumbnailSrc: thumbnailSrc || imageSrc,
        poster: att.posterUrl || att.thumbnailUrl || null,
      });
    } else if (imageSrc) {
      items.push({
        type: 'image',
        fileId: fileId ? String(fileId) : null,
        src: imageSrc,
        linkUrl: String(att.linkUrl || att.link_url || '').trim() || null,
        openInNewTab: att.openInNewTab !== false && att.open_in_new_tab !== false,
      });
    }
  }

  if (!items.length) {
    const cover =
      props.post.secureCoverImageUrl ||
      props.post.imageUrl ||
      props.post.coverImageUrl ||
      (props.post.coverMediaFileId
        ? buildNewsfeedImageProxyUrl(props.post.coverMediaFileId)
        : null);

    if (cover) {
      items.push({
        type: 'image',
        fileId: props.post.coverMediaFileId ? String(props.post.coverMediaFileId) : null,
        src: cover,
      });
    }
  }

  return items;
});

const hasMedia = computed(() => mediaDisplayAllItems.value.length > 0);

const MAX_DISPLAY_TILES = 5;
const displayMediaItems = computed(() => mediaDisplayAllItems.value.slice(0, MAX_DISPLAY_TILES));
const extraMediaCount = computed(() =>
  mediaDisplayAllItems.value.length > MAX_DISPLAY_TILES
    ? mediaDisplayAllItems.value.length - MAX_DISPLAY_TILES
    : 0
);

const displayGridClass = computed(() => {
  const count = displayMediaItems.value.length;
  if (count === 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 grid-rows-2';
  if (count === 4) return 'grid-cols-2 grid-rows-2';
  return 'grid-cols-3';
});

function displayTileClass(index) {
  const count = displayMediaItems.value.length;

  if (count === 1) return 'relative';
  if (count === 2) return 'relative aspect-[4/3]';

  if (count === 3) {
    if (index === 0) return 'relative row-span-2';
    return 'relative';
  }

  if (count === 4) return 'relative aspect-[4/3]';

  if (count >= 5) {
    if (index === 0) return 'relative col-span-2 aspect-[4/3] overflow-hidden';
    if (index === 1) return 'relative aspect-[2/3] overflow-hidden';
    return 'relative aspect-[4/3] overflow-hidden';
  }

  return 'relative';
}

const multiImageFitClass = computed(() => 'object-cover');
const firstVideoTileIndex = computed(() =>
  displayMediaItems.value.findIndex((item) => item?.type === 'video')
);
const activeInlineVideoTileIndex = ref(null);

function isInlinePlayingVideoTile(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return false;
  if (activeInlineVideoTileIndex.value != null) {
    return Number(activeInlineVideoTileIndex.value) === Number(idx);
  }
  return firstVideoTileIndex.value === idx;
}

function playInlineVideoAt(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return;
  activeInlineVideoTileIndex.value = idx;
}

function handleMediaTileClick(item, idx) {
  if (item?.type === 'video') {
    if (isInlinePlayingVideoTile(idx)) {
      return;
    }
    openViewerAt(idx);
    return;
  }
  handleImageActivation(item, idx);
}

function resolveSafeImageLink(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch (_) {
    return null;
  }
}

function handleImageActivation(item, idx) {
  const destination = item?.type === 'image' ? resolveSafeImageLink(item?.linkUrl) : null;
  if (!destination) {
    openViewerAt(idx);
    return;
  }

  if (item.openInNewTab !== false) {
    window.open(destination, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.assign(destination);
}

watch(
  () => displayMediaItems.value.map((item) => `${item?.type || ''}:${item?.fileId || item?.src || ''}`).join('|'),
  () => {
    activeInlineVideoTileIndex.value = null;
  }
);

/* ─────────────────────────────────────────────
 * Dynamic body font size (unchanged)
 * ───────────────────────────────────────────*/
const bodyTextClass = computed(() => {
  const body = rawBody.value;
  if (hasTextBackground.value && body.length <= NEWSFEED_BACKGROUND_MAX_CHARS) return 'text-[30px]';
  if (body.length > 0 && body.length < 85) return 'text-[22px]';
  return 'text-[15px]';
});

/* ─────────────────────────────────────────────
 * Time formatting (unchanged)
 * ───────────────────────────────────────────*/
const createdAtDate = computed(() => {
  const src = props.post.createdAt || props.post.timeAgo;
  if (!src) return null;
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return null;
  return d;
});

const displayTime = computed(() => {
  const d = createdAtDate.value;
  if (!d) return props.post.timeAgo || '';

  const now = new Date();
  let diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hr`;

  return formatShortDate(d);
});

const fullTimeTooltip = computed(() => {
  const d = createdAtDate.value;
  if (!d) return props.post.timeAgo || '';
  return formatFullDate(d);
});

function formatShortDate(d) {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const { hour12, minutes, ampm } = formatTimeParts(d);
  return `${month} ${day} at ${hour12}:${minutes} ${ampm}`;
}

function formatFullDate(d) {
  const weekdays = [
    'Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday',
  ];
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  const weekday = weekdays[d.getDay()];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const { hour12, minutes, ampm } = formatTimeParts(d);

  return `${weekday}, ${month} ${day}, ${year} at ${hour12}:${minutes} ${ampm}`;
}

function formatTimeParts(d) {
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return { hour12: hours, minutes, ampm };
}

/* ─────────────────────────────────────────────
 * Comment bar state (unchanged)
 * ───────────────────────────────────────────*/
const isCommentActive = ref(false);
const commentText = ref('');
const commentTextarea = ref(null);
const commentMediaInput = ref(null);
const pendingCommentMedia = ref(null); // { file, type, previewUrl }
const isUploadingCommentMedia = ref(false);
const showCommentEmojiPicker = ref(false);

const clearPendingCommentMedia = () => {
  const pending = pendingCommentMedia.value;
  if (pending?.previewUrl && String(pending.previewUrl).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(pending.previewUrl);
    } catch {
      // no-op
    }
  }
  pendingCommentMedia.value = null;
  if (commentMediaInput.value) {
    commentMediaInput.value.value = '';
  }
};

const triggerCommentMediaPicker = () => {
  showCommentEmojiPicker.value = false;
  isCommentActive.value = true;
  commentMediaInput.value?.click?.();
};

const toggleCommentEmojiPicker = () => {
  isCommentActive.value = true;
  showCommentEmojiPicker.value = !showCommentEmojiPicker.value;
};

const addCommentEmoji = (emoji) => {
  const value = String(emoji?.i || emoji?.emoji || '');
  if (!value) return;
  commentText.value = `${commentText.value || ''}${value}`;
  showCommentEmojiPicker.value = false;
  nextTick(() => {
    commentTextarea.value?.focus?.();
    autoResize({ target: commentTextarea.value });
  });
};

const handleCommentMediaPicked = (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const mime = String(file.type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  if (!isImage && !isVideo) {
    if (typeof notify === 'function') {
      notify({
        title: 'Unsupported file',
        body: 'Please choose an image or video.',
        variant: 'error',
      });
    }
    if (commentMediaInput.value) commentMediaInput.value.value = '';
    return;
  }

  clearPendingCommentMedia();
  pendingCommentMedia.value = {
    file,
    type: isVideo ? 'video' : 'image',
    previewUrl: URL.createObjectURL(file),
  };
};

const uploadPendingCommentMedia = async () => {
  const file = pendingCommentMedia.value?.file || null;
  if (!file) return [];
  isUploadingCommentMedia.value = true;
  try {
    const uploaded = await mediaUploadStore.uploadSingleFile(
      file,
      null,
      { origin: 'newsfeedComment' }
    );
    const attachment = normalizeCommentAttachmentPayload(uploaded);
    return attachment ? [attachment] : null;
  } finally {
    isUploadingCommentMedia.value = false;
  }
};

const uploadCommentMediaFile = async (file) => {
  if (!file) return [];
  isUploadingCommentMedia.value = true;
  try {
    const uploaded = await mediaUploadStore.uploadSingleFile(
      file,
      null,
      { origin: 'newsfeedComment' }
    );
    const attachment = normalizeCommentAttachmentPayload(uploaded);
    return attachment ? [attachment] : null;
  } finally {
    isUploadingCommentMedia.value = false;
  }
};

const normalizeVideoReadyAttachments = (raw) => {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

const onCommentVideoReady = (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const targetCommentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(targetCommentId) || targetCommentId <= 0) return;

  const readyAttachments = normalizeVideoReadyAttachments(payload?.attachments ?? payload?.attachment);
  if (!readyAttachments.length) return;

  let changed = false;
  comments.value = (comments.value || []).map((comment) => {
    if (Number(comment?.id || 0) !== targetCommentId) return comment;
    changed = true;
    return {
      ...comment,
      mediaProcessing: false,
      media_processing: false,
      attachments: readyAttachments,
      attachment: readyAttachments,
      attachment_json: JSON.stringify(readyAttachments),
    };
  });

  if (changed && payload?.selfReady && typeof notify === 'function') {
    notify({
      title: 'Video ready',
      body: 'Your video is now ready to be viewed.',
      variant: 'success',
    });
  }
};

let unsubscribeCommentVideoReady = null;
let unsubscribeCommentCreated = null;
let unsubscribeCommentUpdated = null;
let unsubscribeCommentDeleted = null;
let unsubscribeCommentReactionUpdated = null;
const seenWsCommentIds = new Set();
const seenWsCommentMutationKeys = new Set();
const seenWsCommentReactionKeys = new Set();

const onCommentCreated = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;
  const commentKey = String(commentId);
  if (seenWsCommentIds.has(commentKey)) return;
  seenWsCommentIds.add(commentKey);

  const existsInComments = (comments.value || []).some((c) => Number(c?.id || 0) === commentId);
  const existsInLatestReply = Object.values(latestReplyByParentId.value || {}).some(
    (r) => Number(r?.id || 0) === commentId
  );
  if (existsInComments || existsInLatestReply) return;

  localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;

  const shouldRefreshPreview =
    commentsLoaded.value ||
    isCommentActive.value ||
    Number(openReplyForCommentId.value || 0) > 0;

  if (shouldRefreshPreview) {
    await loadCommentsForPost();
    scheduleComputeAllThreadLineBottoms();
  }
};

const onCommentUpdated = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || payload?.comment?.id || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;
  const mutationKey = `upd:${commentId}:${String(payload?.updatedAt || payload?.comment?.updatedAt || '')}`;
  if (seenWsCommentMutationKeys.has(mutationKey)) return;
  seenWsCommentMutationKeys.add(mutationKey);

  const shouldRefreshPreview =
    commentsLoaded.value ||
    isCommentActive.value ||
    Number(openReplyForCommentId.value || 0) > 0;
  if (shouldRefreshPreview) {
    await loadCommentsForPost();
    scheduleComputeAllThreadLineBottoms();
  }
};

const onCommentDeleted = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;
  const mutationKey = `del:${commentId}:${String(payload?.deletedAt || '')}:${Number(payload?.deletedCount || 0)}`;
  if (seenWsCommentMutationKeys.has(mutationKey)) return;
  seenWsCommentMutationKeys.add(mutationKey);

  const deletedCount = Math.max(1, Number(payload?.deletedCount || 0));
  localCommentsCount.value = Math.max(0, Number(localCommentsCount.value || 0) - deletedCount);

  const shouldRefreshPreview =
    commentsLoaded.value ||
    isCommentActive.value ||
    Number(openReplyForCommentId.value || 0) > 0;
  if (shouldRefreshPreview) {
    await loadCommentsForPost();
    scheduleComputeAllThreadLineBottoms();
  }
};

const mergeReactionPayloadIntoEntity = (entity, data) => {
  if (!entity || typeof entity !== 'object') return entity;

  const nextSummary =
    data?.reactions && typeof data.reactions === 'object'
      ? {
          total: Number(data.reactions.total) || 0,
          like: Number(data.reactions.like) || 0,
          love: Number(data.reactions.love) || 0,
          care: Number(data.reactions.care) || 0,
          haha: Number(data.reactions.haha) || 0,
          wow: Number(data.reactions.wow) || 0,
          sad: Number(data.reactions.sad) || 0,
          angry: Number(data.reactions.angry) || 0,
        }
      : (entity.reactionsSummary || entity.reactions_summary || null);

  const nextUsersByType =
    data?.reactionUsersByType && typeof data.reactionUsersByType === 'object'
      ? data.reactionUsersByType
      : (entity.reactionUsersByType || {
          like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
        });

  return {
    ...entity,
    reactionsSummary:
      nextSummary || entity.reactionsSummary || {
        total: Number(entity.reactionsCount) || 0,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      },
    reactionsCount:
      nextSummary && typeof nextSummary.total === 'number'
        ? nextSummary.total
        : (Number(entity.reactionsCount) || 0),
    reactionUsersByType:
      nextUsersByType || entity.reactionUsersByType || {
        like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
      },
  };
};

const applyCommentReactionEventToLocalState = (commentId, data) => {
  const targetId = Number(commentId);
  if (!Number.isFinite(targetId) || targetId <= 0) return false;

  let changed = false;

  const idx = findCommentIndexById(targetId);
  if (idx >= 0) {
    patchCommentFromReactionResponse(targetId, data);
    changed = true;
  }

  const latestMap = latestReplyByParentId.value || {};
  let latestChanged = false;
  const nextLatestMap = { ...latestMap };
  for (const key of Object.keys(latestMap)) {
    const item = latestMap[key];
    if (Number(item?.id) !== targetId) continue;
    nextLatestMap[key] = mergeReactionPayloadIntoEntity(item, data);
    latestChanged = true;
  }
  if (latestChanged) {
    latestReplyByParentId.value = nextLatestMap;
    changed = true;
  }

  return changed;
};

const onCommentReactionUpdated = (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;

  const reactionKey = `react:${commentId}:${String(payload?.createdAt || '')}:${String(payload?.authorUserType || '')}:${Number(payload?.authorUserId || 0)}`;
  if (seenWsCommentReactionKeys.has(reactionKey)) return;
  seenWsCommentReactionKeys.add(reactionKey);

  applyCommentReactionEventToLocalState(commentId, {
    reactions: payload?.reactions,
    reactionUsersByType: payload?.reactionUsersByType,
  });
};

const resetCommentTextareaHeight = async () => {
  await nextTick();

  const el = commentTextarea.value;
  if (!el) return;

  // Clear any stale inline height set by autoResize()
  el.style.height = '';

  // When collapsed, force the pill height so placeholder never clips
  if (!isCommentActive.value) {
    el.style.height = '36px'; // matches h-9
  }
};


const handleCommentBlur = () => {
  const hasPendingMedia = !!pendingCommentMedia.value?.file;
  if (!commentText.value.trim() && !hasPendingMedia && !isUploadingCommentMedia.value) {
    isCommentActive.value = false;
    resetCommentTextareaHeight();
  }
  showCommentEmojiPicker.value = false;
};


const autoResize = (event) => {
  const el = event?.target || commentTextarea.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
};

const hasEngagement = computed(() => {
  return hasReactions.value || hasComments.value;
});

/* ─────────────────────────────────────────────
 * Collapsible body (unchanged)
 * ───────────────────────────────────────────*/
const showFullBody = ref(false);

const PREVIEW_MAX_LINES = 2;
const PREVIEW_MAX_CHARS = 140;

const bodyPreviewText = computed(() => {
  const body = rawBody.value;
  if (!body) return '';

  const lines = body.split(/\r?\n/);

  let result = '';
  let linesUsed = 0;

  for (let i = 0; i < lines.length && linesUsed < PREVIEW_MAX_LINES; i++) {
    const line = lines[i];
    const prefix = linesUsed === 0 ? '' : '\n';

    const projectedLength = result.length + prefix.length + line.length;

    if (projectedLength > PREVIEW_MAX_CHARS) {
      const remaining = PREVIEW_MAX_CHARS - result.length - prefix.length;
      if (remaining > 0) result += prefix + line.slice(0, remaining);
      linesUsed++;
      break;
    } else {
      result += prefix + line;
      linesUsed++;
    }
  }

  result = result.replace(/[\s\n]+$/g, '');

  if (result.length < body.length) return `${result}…`;
  return result;
});

const isBodyLong = computed(() => {
  if (hasTextBackground.value) return false;
  return bodyPreviewText.value.length < rawBody.value.length;
});

const displayBodyText = computed(() => {
  const body = rawBody.value;
  if (hasTextBackground.value) return body;
  if (!isBodyLong.value || showFullBody.value) return body;
  return bodyPreviewText.value;
});

function escapeHtml(raw) {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function linkifyText(raw) {
  const escaped = escapeHtml(raw);
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  return escaped.replace(urlRegex, (fullMatch) => {
    let match = fullMatch;
    let trailing = '';
    while (/[),.!?:;]+$/.test(match)) {
      trailing = match.slice(-1) + trailing;
      match = match.slice(0, -1);
    }

    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#2D8CFF] font-medium hover:underline break-all">${match}</a>${trailing}`;
  });
}

const displayBodyHtml = computed(() => linkifyText(displayBodyText.value));

/* ─────────────────────────────
 * Media viewer state (unchanged)
 * ─────────────────────────────*/
const viewerOpen = ref(false);
const viewerIndex = ref(0);
const viewerMediaOverrideItems = ref(null);

const normalizeViewerMediaItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      type: item?.type,
      src: item?.src,
      fileId: item?.fileId || null,
      poster: item?.poster || item?.thumbnailSrc || null,
      thumbnailSrc: item?.thumbnailSrc || null,
    }))
    .filter((item) => item.type && item.src);
};

const viewerMediaItems = computed(() => {
  if (Array.isArray(viewerMediaOverrideItems.value) && viewerMediaOverrideItems.value.length) {
    return viewerMediaOverrideItems.value;
  }
  return normalizeViewerMediaItems(mediaDisplayAllItems.value);
});

const openViewerAt = (idx) => {
  viewerMediaOverrideItems.value = null;
  openViewerWithItems(mediaDisplayAllItems.value, idx);
};

const openViewerWithItems = (items, idx = 0) => {
  const normalizedItems = normalizeViewerMediaItems(items);
  if (!normalizedItems.length) return;
  viewerMediaOverrideItems.value = normalizedItems;
  if (!viewerMediaItems.value.length) return;

  const max = viewerMediaItems.value.length - 1;
  const safeIndex = Math.min(Math.max(idx, 0), max);

  viewerIndex.value = safeIndex;
  viewerOpen.value = true;
};

/* ─────────────────────────────
 * Reaction picker open/close (unchanged)
 * ─────────────────────────────*/
const isReactionDockOpen = ref(false);

let reactionOpenTimer = null;
let reactionCloseTimer = null;

const handleReactionMouseEnter = () => {
  if (reactionCloseTimer) {
    clearTimeout(reactionCloseTimer);
    reactionCloseTimer = null;
  }

  if (isReactionDockOpen.value) return;

  if (reactionOpenTimer) clearTimeout(reactionOpenTimer);
  reactionOpenTimer = setTimeout(() => {
    isReactionDockOpen.value = true;
    reactionOpenTimer = null;
  }, 200);
};

const handleReactionMouseLeave = () => {
  if (reactionOpenTimer) {
    clearTimeout(reactionOpenTimer);
    reactionOpenTimer = null;
  }

  if (!isReactionDockOpen.value) return;

  if (reactionCloseTimer) clearTimeout(reactionCloseTimer);
  reactionCloseTimer = setTimeout(() => {
    isReactionDockOpen.value = false;
    reactionCloseTimer = null;
  }, 250);
};

const isSubmittingReaction = ref(false);

const handleReactionSelect = async (type) => {
  console.log('[NewsfeedCreatorPost] reaction selected:', type, 'for post', props.post.id);

  // Close the dock immediately for UX
  isReactionDockOpen.value = false;

  if (!REACTION_META[type]) {
    console.warn('[NewsfeedCreatorPost] unknown reaction type:', type);
    return;
  }

  if (isSubmittingReaction.value) return;
  isSubmittingReaction.value = true;

  try {
    const postId = props.post.id;

    const resp = await fetch(`${apiProxyUrl}/api/creator-newsfeed/posts/${postId}/react`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reactionType: type }),
    });

    if (!resp.ok) {
      const text = await resp.text();
      console.error('[NewsfeedCreatorPost] reaction API error:', resp.status, text);
      return;
    }

    const data = await resp.json();
    console.log('[NewsfeedCreatorPost] reaction API response:', data);

    // Update counts from backend response
    if (data && data.reactions && typeof data.reactions.total === 'number') {
      localReactionsCount.value = data.reactions.total;

      localReactionsSummary.value = {
        total: data.reactions.total || 0,
        like: data.reactions.like || 0,
        love: data.reactions.love || 0,
        care: data.reactions.care || 0,
        haha: data.reactions.haha || 0,
        wow: data.reactions.wow || 0,
        sad: data.reactions.sad || 0,
        angry: data.reactions.angry || 0,
      };
    }

    // Update tooltip names locally (so UI responds instantly)
    // Since user has ONE reaction per post, keep "me" only under selected type.
    const me = currentUserDisplayName.value;
    if (me) {
      const next = { ...(localReactionNamesByType.value || {}) };

      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(next[k]) ? next[k] : [];
        next[k] = arr.filter((n) => String(n) !== String(me));
      }

      const selectedArr = Array.isArray(next[type]) ? next[type] : [];
      next[type] = [me, ...selectedArr];

      localReactionNamesByType.value = next;
    }

    emit('react', {
      postId: props.post.id,
      reactionType: type,
      summary: data && data.reactions ? data.reactions : null,
    });
  } catch (err) {
    console.error('[NewsfeedCreatorPost] error sending reaction:', err);
  } finally {
    isSubmittingReaction.value = false;
  }
};

const handleLikeButtonClick = async () => {
  console.log('[NewsfeedCreatorPost] Like button clicked for post', props.post.id);

  // If we're already in-flight with a reaction, ignore
  if (isSubmittingReaction.value) return;

  // If user has NO current reaction, behave like normal "Like"
  if (!currentUserReactionType.value) {
    return handleReactionSelect('like');
  }

  // User already has some reaction → toggle it off (unlike)
  isSubmittingReaction.value = true;

  try {
    const postId = props.post.id;

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${postId}/react`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    if (!resp.ok) {
      const text = await resp.text();
      console.error('[NewsfeedCreatorPost] unlike API error:', resp.status, text);
      return;
    }

    const data = await resp.json();
    console.log('[NewsfeedCreatorPost] unlike API response:', data);

    // Update counts from backend response (same shape as POST /react)
    if (data && data.reactions && typeof data.reactions.total === 'number') {
      localReactionsCount.value = data.reactions.total;

      localReactionsSummary.value = {
        total: data.reactions.total || 0,
        like: data.reactions.like || 0,
        love: data.reactions.love || 0,
        care: data.reactions.care || 0,
        haha: data.reactions.haha || 0,
        wow: data.reactions.wow || 0,
        sad: data.reactions.sad || 0,
        angry: data.reactions.angry || 0,
      };
    }

    // Remove the current user from all tooltip name lists
    const me = currentUserDisplayName.value;
    if (me) {
      const next = { ...(localReactionNamesByType.value || {}) };

      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(next[k]) ? next[k] : [];
        next[k] = arr.filter((n) => String(n) !== String(me));
      }

      localReactionNamesByType.value = next;
    }

    // Emit event upwards (optional but matches existing pattern)
    emit('react', {
      postId: props.post.id,
      reactionType: null,
      summary: data && data.reactions ? data.reactions : null,
      removed: true,
    });
  } catch (err) {
    console.error('[NewsfeedCreatorPost] error removing reaction:', err);
  } finally {
    isSubmittingReaction.value = false;
  }
};


const reactionTooltipPosition = ref({}); // e.g. { like: 'above', love: 'below' }
const commentReactionTooltipPosition = ref({});

const handleReactionTooltipEnter = (type, event) => {
  if (typeof window === 'undefined' || !event?.currentTarget) return;

  const triggerEl = event.currentTarget;
  const rect = triggerEl.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Rough estimate of tooltip height in px (enough for above/below decision)
  const estimatedTooltipHeight = 380;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // If there isn't enough room below and there's more room above, flip it
  const showAbove =
    spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;

  reactionTooltipPosition.value = {
    ...reactionTooltipPosition.value,
    [type]: showAbove ? 'above' : 'below',
  };
};

const resolveTooltipPlacement = (event, estimatedTooltipHeight = 380) => {
  if (typeof window === 'undefined' || !event?.currentTarget) return 'below';
  const rect = event.currentTarget.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const showAbove = spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;
  return showAbove ? 'above' : 'below';
};

const handleCommentReactionTooltipEnter = (commentId, event) => {
  if (!commentId) return;
  commentReactionTooltipPosition.value = {
    ...commentReactionTooltipPosition.value,
    [String(commentId)]: resolveTooltipPlacement(event),
  };
};

const MAX_TOOLTIP_NAMES = 20;

const tooltipNamesForType = (type) => {
  const rawNames = Array.isArray(localReactionNamesByType.value?.[type])
    ? localReactionNamesByType.value[type]
    : [];

  return rawNames
    .filter((n) => typeof n === 'string' && n.trim().length > 0)
    .map((n) => n.trim())
    .slice(0, MAX_TOOLTIP_NAMES);
};

const tooltipExtraCountForType = (type) => {
  const total = Number(localReactionsSummary.value?.[type]) || 0;
  const shown = tooltipNamesForType(type).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

const tooltipFallbackText = (type) => {
  const total = Number(localReactionsSummary.value?.[type]) || 0;
  // If we already have names, no need for fallback text
  if (!total || tooltipNamesForType(type).length) return '';
  return total === 1 ? '1 person reacted' : `${total} people reacted`;
};

const isSavingPost = ref(false);

async function handleSavePost(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn('[NewsfeedCreatorPost] handleSavePost: no postId found');
    return;
  }

  if (isSavingPost.value) return;
  isSavingPost.value = true;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}), // backend doesn't need anything extra
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorPost] save-post backend NOT OK:',
        res.status,
        text
      );
      alert('Failed to save post. Please try again.');
      return;
    }

    const data = await res.json().catch(() => ({}));
    console.log('[NewsfeedCreatorPost] save-post OK:', data);

    // Let parent (Discussion tab) mark this post as saved
    emit('save-post', { postId, backend: data });

  } catch (err) {
    console.error('[NewsfeedCreatorPost] save-post error:', err);
    alert('Error saving post. Please try again.');
  } finally {
    isSavingPost.value = false;
  }
}

function handleUnsavePost(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn('[NewsfeedCreatorPost] handleUnsavePost: no postId found');
    return;
  }

  // Just bubble up; parents (Discussion / Saved) will call the backend
  emit('unsave-post', { postId });
}

const comments = ref([]);
const commentsLoaded = ref(false);
const commentsLoading = ref(false);
const isSubmittingComment = ref(false);
const SHOW_COMMENT_REPLIES_UI = true; // UI only for now (assume 1 reply)

// Track comments created in this session so we can show them
// even while keeping MVP "show 1 comment" behavior for the rest.
const localAddedCommentIds = ref(new Set());


const COMMENTS_PAGE_SIZE = 20;

async function loadCommentsForPost() {
  const postId = props.post?.id;
  if (!postId) return;

  if (commentsLoading.value) return;
  commentsLoading.value = true;

  try {
    const url = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments?limit=${COMMENTS_PAGE_SIZE}&offset=0`;

    const resp = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsfeedCreatorPost] loadComments NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const list = Array.isArray(data?.comments) ? data.comments : [];

    // ✅ normalize like slug modal (server-backed fields)
      const normalized = list.map((c) => ({
        ...c,
        reactionsSummary: c.reactionsSummary || c.reactions_summary || null,
        reactionsCount:
          typeof c.reactionsCount === 'number'
            ? c.reactionsCount
            : (Number(c.reactions_count) || 0),
        reactionUsersByType:
          c.reactionUsersByType || c.reaction_users_by_type || c.reactionUsers || null,
        myReactionType: c.myReactionType || c.my_reaction_type || null,
      }));

      comments.value = normalized;
      commentsLoaded.value = true;

  } catch (err) {
    console.error('[NewsfeedCreatorPost] loadComments error:', err);
  } finally {
    commentsLoading.value = false;
  }
}

async function submitComment() {
  const postId = props.post?.id;
  if (!postId) return;

  const body = String(commentText.value || '').trim();
  const hasMedia = !!pendingCommentMedia.value?.file;
  if (!body && !hasMedia) return;

  if (isSubmittingComment.value) return;
  isSubmittingComment.value = true;

  try {
    const pendingMediaSnapshot = pendingCommentMedia.value
      ? {
          file: pendingCommentMedia.value.file,
          type: pendingCommentMedia.value.type,
        }
      : null;
    const shouldOptimisticVideo = !!(pendingMediaSnapshot?.file && pendingMediaSnapshot?.type === 'video');
    let optimisticTempId = null;

    if (shouldOptimisticVideo) {
      optimisticTempId = `temp-video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimisticComment = {
        id: optimisticTempId,
        postId,
        parentCommentId: null,
        userId: props.post?.currentUserId || null,
        userType: props.post?.currentUserType || 'creator',
        body,
        attachments: [],
        mediaProcessing: true,
        createdAt: new Date().toISOString(),
        authorName: props.post?.currentUserName || props.postingAsName || 'You',
        authorAvatarUrl: commenterAvatarSrc.value || defaultAuthorAvatar,
        reactionsSummary: {
          total: 0, like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
        },
        reactionsCount: 0,
        reactionUsersByType: {
          like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
        },
        myReactionType: null,
      };

      comments.value = [...(comments.value || []), optimisticComment];
      const optimisticIds = new Set(localAddedCommentIds.value || []);
      optimisticIds.add(optimisticTempId);
      localAddedCommentIds.value = optimisticIds;
      localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;

      commentText.value = '';
      showCommentEmojiPicker.value = false;
      clearPendingCommentMedia();
      isCommentActive.value = false;
      resetCommentTextareaHeight();

      void (async () => {
        let createdCommentId = null;
        try {
          const uploaded = await uploadCommentMediaFile(pendingMediaSnapshot.file);
          if (uploaded === null) throw new Error('upload_failed');
          const commentAttachments = uploaded;

          const targetUrl = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments`;
          const payload = { body };
          if (commentAttachments.length) {
            payload.attachments = commentAttachments;
            payload.attachment = commentAttachments;
            payload.attachment_json = JSON.stringify(commentAttachments);
          }

          let resp = await fetch(targetUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });

          if (!resp.ok && commentAttachments.length) {
            resp = await fetch(targetUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ body }),
            });
          }
          if (!resp.ok) throw new Error('comment_create_failed');

          const data = await resp.json().catch(() => ({}));
          const newComment = data?.comment || null;
          if (!newComment) throw new Error('comment_missing');
          createdCommentId = newComment.id;

          const normalizedNewComment = {
            ...newComment,
            reactionsSummary: newComment.reactionsSummary || {
              total: 0, like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
            },
            reactionsCount: Number(newComment.reactionsCount) || 0,
            reactionUsersByType: newComment.reactionUsersByType || {
              like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
            },
            myReactionType: newComment.myReactionType || null,
          };

          if (!Array.isArray(normalizedNewComment.attachments) && commentAttachments.length) {
            normalizedNewComment.attachments = commentAttachments;
          }

          comments.value = (comments.value || []).map((c) =>
            String(c?.id) === String(optimisticTempId) ? normalizedNewComment : c
          );

        } catch (error) {
          comments.value = (comments.value || []).filter((c) => String(c?.id) !== String(optimisticTempId));
          localCommentsCount.value = Math.max(0, (Number(localCommentsCount.value) || 0) - 1);
          if (typeof notify === 'function') {
            notify({
              title: 'Upload failed',
              body: 'Could not upload media. Please try again.',
              variant: 'error',
            });
          }
          console.error('[NewsfeedCreatorPost] optimistic video comment failed:', error);
        } finally {
          const next = new Set(localAddedCommentIds.value || []);
          next.delete(optimisticTempId);
          if (createdCommentId != null) next.add(createdCommentId);
          localAddedCommentIds.value = next;
        }
      })();

      return;
    }

    let commentAttachments = [];
    if (hasMedia) {
      const uploaded = await uploadPendingCommentMedia();
      if (uploaded === null) {
        if (typeof notify === 'function') {
          notify({
            title: 'Upload failed',
            body: 'Could not upload media. Please try again.',
            variant: 'error',
          });
        }
        return;
      }
      commentAttachments = uploaded;
    }

    const targetUrl = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments`;
    const payload = { body };
    if (commentAttachments.length) {
      payload.attachments = commentAttachments;
      payload.attachment = commentAttachments;
      payload.attachment_json = JSON.stringify(commentAttachments);
    }

    let resp = await fetch(targetUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!resp.ok && commentAttachments.length) {
      // Backward-compatible fallback for backends that only accept { body }.
      resp = await fetch(targetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body }),
      });
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsfeedCreatorPost] submitComment NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const newComment = data?.comment || null;

    // ✅ ensure new comments have reaction fields (so template + getters work)
const normalizedNewComment = newComment
  ? {
      ...newComment,
      reactionsSummary: newComment.reactionsSummary || {
        total: 0,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      },
      reactionsCount: Number(newComment.reactionsCount) || 0,
      reactionUsersByType: newComment.reactionUsersByType || {
        like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
      },
      myReactionType: newComment.myReactionType || null,
    }
  : null;

    if (
      normalizedNewComment &&
      commentAttachments.length &&
      !Array.isArray(normalizedNewComment.attachments)
    ) {
      normalizedNewComment.attachments = commentAttachments;
    }


    // optimistic prepend (backend returns authorName/avatar)
    if (newComment) {
      comments.value = [...(comments.value || []), normalizedNewComment];

      // ✅ mark this comment as locally added (so it will display)
      const next = new Set(localAddedCommentIds.value);
      next.add(newComment.id);
      localAddedCommentIds.value = next;

      // ✅ keep the visible count in sync immediately
      localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;
      commentText.value = '';
      showCommentEmojiPicker.value = false;
      clearPendingCommentMedia();
      isCommentActive.value = false;
      resetCommentTextareaHeight();
      return;
    } else {
      // fallback: reload (shouldn’t normally happen)
      await loadCommentsForPost();
      localCommentsCount.value = Math.max(
        Number(localCommentsCount.value) || 0,
        Array.isArray(comments.value) ? comments.value.length : 0
      );
    }

    commentText.value = '';
    showCommentEmojiPicker.value = false;
    clearPendingCommentMedia();
    isCommentActive.value = false;
    resetCommentTextareaHeight();
  } catch (err) {
    console.error('[NewsfeedCreatorPost] submitComment error:', err);
  } finally {
    isSubmittingComment.value = false;
  }
}

function formatCommentTime(src) {
  if (!src) return '';
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  let diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} d`;

  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 4) return `${diffWk} w`;

  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo} mo`;

  const diffYr = Math.floor(diffDay / 365);
  return `${diffYr} y`;
}

function formatCommentFullTooltip(src) {
  if (!src) return '';
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return '';
  return formatFullDate(d);
}


// ─────────────────────────────────────────────
// Comment reactions (SERVER-BACKED, MUTABLE) ✅
// Mirrors NewsFeedFullPostSlug behavior
// Comments returned from backend include:
//   reactionsCount, reactionsSummary, reactionUsersByType, myReactionType
// ─────────────────────────────────────────────
const openCommentReactionForId = ref(null);

// hover open/close timers (same feel as post)
let commentReactionOpenTimer = null;
let commentReactionCloseTimer = null;

const handleCommentReactionMouseEnter = (commentId) => {
  if (commentReactionCloseTimer) {
    clearTimeout(commentReactionCloseTimer);
    commentReactionCloseTimer = null;
  }

  if (openCommentReactionForId.value === commentId) return;

  if (commentReactionOpenTimer) clearTimeout(commentReactionOpenTimer);
  commentReactionOpenTimer = setTimeout(() => {
    openCommentReactionForId.value = commentId;
    commentReactionOpenTimer = null;
  }, 200);
};

const handleCommentReactionMouseLeave = (commentId) => {
  if (commentReactionOpenTimer) {
    clearTimeout(commentReactionOpenTimer);
    commentReactionOpenTimer = null;
  }

  if (openCommentReactionForId.value !== commentId) return;

  if (commentReactionCloseTimer) clearTimeout(commentReactionCloseTimer);
  commentReactionCloseTimer = setTimeout(() => {
    if (openCommentReactionForId.value === commentId) {
      openCommentReactionForId.value = null;
    }
    commentReactionCloseTimer = null;
  }, 250);
};

// click-away close (same as modal)
const handleGlobalClickToCloseCommentPicker = () => {
  if (openCommentReactionForId.value !== null) {
    openCommentReactionForId.value = null;
  }
};

onMounted(() => {
  window.addEventListener('click', handleGlobalClickToCloseCommentPicker);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', handleGlobalClickToCloseCommentPicker);

  if (commentReactionOpenTimer) clearTimeout(commentReactionOpenTimer);
  if (commentReactionCloseTimer) clearTimeout(commentReactionCloseTimer);
});

// ---- comment lookup helpers
const findCommentIndexById = (commentId) => {
  const list = Array.isArray(comments.value) ? comments.value : [];
  return list.findIndex((c) => Number(c?.id) === Number(commentId));
};

const getCommentById = (commentId) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return null;
  return comments.value[idx] || null;
};

// ---- getters used by template
const getCommentReactionType = (commentId) => {
  const c = getCommentById(commentId);
  return c?.myReactionType || null;
};

const getCommentReactionCount = (commentId) => {
  const c = getCommentById(commentId);
  const total = Number(c?.reactionsSummary?.total);
  if (Number.isFinite(total)) return total;
  return Number(c?.reactionsCount) || 0;
};

const getCommentReactionRows = (commentId) => {
  const c = getCommentById(commentId);
  const summary = c?.reactionsSummary || c?.reactions_summary || {};
  return REACTION_KEYS
    .map((type, index) => ({
      type,
      count: Number(summary?.[type]) || 0,
      index,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.index - b.index;
    });
};

const getCommentTopReactionTypes = (commentId) =>
  getCommentReactionRows(commentId).slice(0, 2).map((row) => row.type);

const getCommentReactionTooltipRows = (commentId) =>
  getCommentReactionRows(commentId);

const MAX_COMMENT_TOOLTIP_NAMES = 20;

const getCommentReactionTooltipNames = (commentId) => {
  const rows = getCommentReactionRows(commentId);
  if (rows.length !== 1) return [];
  const c = getCommentById(commentId);
  const myType = rows[0].type;

  const arr = c?.reactionUsersByType?.[myType];
  const users = Array.isArray(arr) ? arr : [];

  // backend shape: [{ userId, userType, name }] or sometimes strings
  const names = users
    .map((u) => (u?.name ? String(u.name).trim() : String(u || '').trim()))
    .filter((n) => n.length);

  return names.slice(0, MAX_COMMENT_TOOLTIP_NAMES);
};

const getCommentReactionTooltipText = (commentId) => {
  const total = getCommentReactionCount(commentId);
  if (total <= 0) return '';
  if (getCommentReactionRows(commentId).length > 1) return '';

  const names = getCommentReactionTooltipNames(commentId);

  if (!names.length) return total === 1 ? '1 person' : `${total} people`;
  if (total <= names.length) return names.join(', ');

  const extra = total - names.length;
  return `${names.join(', ')} and ${extra} more`;
};

const getCommentReactionTooltipExtraCount = (commentId) => {
  const total = getCommentReactionCount(commentId);
  const shown = getCommentReactionTooltipNames(commentId).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

// ---- patch helpers (same as modal)
const patchCommentFromReactionResponse = (commentId, data) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const existing = comments.value[idx] || {};

  const nextSummary =
    data?.reactions && typeof data.reactions === 'object'
      ? {
          total: data.reactions.total || 0,
          like: data.reactions.like || 0,
          love: data.reactions.love || 0,
          care: data.reactions.care || 0,
          haha: data.reactions.haha || 0,
          wow: data.reactions.wow || 0,
          sad: data.reactions.sad || 0,
          angry: data.reactions.angry || 0,
        }
      : (existing.reactionsSummary || existing.reactions_summary || null);

  const nextMyReactionType =
    typeof data?.myReactionType !== 'undefined'
      ? data.myReactionType
      : existing.myReactionType;

  const nextUsersByType =
    data?.reactionUsersByType && typeof data.reactionUsersByType === 'object'
      ? data.reactionUsersByType
      : (existing.reactionUsersByType || {
          like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
        });

  const next = {
    ...existing,

    reactionsSummary:
      nextSummary ||
      existing.reactionsSummary || {
        total: Number(existing.reactionsCount) || 0,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      },

    reactionsCount:
      nextSummary && typeof nextSummary.total === 'number'
        ? nextSummary.total
        : (Number(existing.reactionsCount) || 0),

    reactionUsersByType:
      nextUsersByType ||
      existing.reactionUsersByType || {
        like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
      },

    myReactionType: nextMyReactionType || null,
  };

  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  list[idx] = next;
  comments.value = list;
};

const isSubmittingCommentReaction = ref(false);

const handleCommentReactionSelect = async (commentId, type) => {
  console.log('[NewsfeedCreatorPost] comment reaction selected:', type, 'for comment', commentId);

  openCommentReactionForId.value = null;

  if (!REACTION_META[type]) {
    console.warn('[NewsfeedCreatorPost] unknown comment reaction type:', type);
    return;
  }

  if (isSubmittingCommentReaction.value) return;
  isSubmittingCommentReaction.value = true;

  let prevType = null;
  let optimisticApply = () => {};

  try {
    prevType = getCommentReactionType(commentId);
    const me = String(currentUserDisplayName.value || props.postingAsName || 'You');

    optimisticApply = (newType) => {
      const idx = findCommentIndexById(commentId);
      if (idx < 0) return;

      const list = Array.isArray(comments.value) ? [...comments.value] : [];
      const c = list[idx] || {};

      const usersByType =
        c.reactionUsersByType && typeof c.reactionUsersByType === 'object'
          ? JSON.parse(JSON.stringify(c.reactionUsersByType))
          : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };

      // remove me from all types
      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(usersByType[k]) ? usersByType[k] : [];
        usersByType[k] = arr.filter((u) => String(u?.name || u) !== me);
      }

      const summary =
        c.reactionsSummary && typeof c.reactionsSummary === 'object'
          ? { ...c.reactionsSummary }
          : {
              total: Number(c.reactionsCount) || 0,
              like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
            };

      // decrement previous type bucket (if any)
      if (prevType && Number(summary[prevType] || 0) > 0) {
        summary[prevType] = Number(summary[prevType] || 0) - 1;
      }

      if (newType) {
        summary[newType] = Number(summary[newType] || 0) + 1;

        // total only changes if adding/removing (switch keeps total same)
        if (!prevType) summary.total = Number(summary.total || 0) + 1;
      } else {
        if (prevType) summary.total = Math.max(0, Number(summary.total || 0) - 1);
      }

      if (newType) {
        const arr = Array.isArray(usersByType[newType]) ? usersByType[newType] : [];
        usersByType[newType] = [{ name: me }, ...arr];
      }

      list[idx] = {
        ...c,
        myReactionType: newType || null,
        reactionsSummary: summary,
        reactionsCount: Number(summary.total) || 0,
        reactionUsersByType: usersByType,
      };

      comments.value = list;
    };

    // Toggle off if selecting same type
    if (prevType && prevType === type) {
      optimisticApply(null);

      const resp = await fetch(
        `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(commentId)}/react`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        console.error('[NewsfeedCreatorPost] DELETE comment react NOT OK:', resp.status, text);
        optimisticApply(prevType || null);
        return;
      }

      let data = {};
      try {
        const text = await resp.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      patchCommentFromReactionResponse(commentId, {
        ...data,
        myReactionType: null,
        reactionType: null,
      });

      return;
    }

    // Otherwise POST (create/update)
    optimisticApply(type);

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(commentId)}/react`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reactionType: type }),
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsfeedCreatorPost] POST comment react NOT OK:', resp.status, text);
      optimisticApply(prevType || null);
      return;
    }

    let data = {};
    try {
      const text = await resp.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = {};
    }

    patchCommentFromReactionResponse(commentId, {
      ...data,
      myReactionType: type,
      reactionType: type,
    });
  } catch (err) {
    console.error('[NewsfeedCreatorPost] handleCommentReactionSelect error:', err);
    optimisticApply(prevType || null);
  } finally {
    isSubmittingCommentReaction.value = false;
  }
};


const sortedCommentsOldestFirst = computed(() => {
  const list = Array.isArray(comments.value) ? [...comments.value] : [];

  list.sort((a, b) => {
    const ta = new Date(a?.createdAt || a?.created_at || 0).getTime();
    const tb = new Date(b?.createdAt || b?.created_at || 0).getTime();
    return ta - tb; // oldest -> newest
  });

  return list;
});

// MVP: show ONLY the oldest comment
const visibleComments = computed(() => {
  const sorted = sortedCommentsOldestFirst.value;
  if (!sorted.length) return [];

  const first = sorted[0];
  const ids = localAddedCommentIds.value || new Set();

  // Keep MVP: always include the first comment
  const out = [first];

  // Then include ONLY comments added locally in this session
  for (const c of sorted) {
    if (!c || !c.id) continue;
    if (c.id === first.id) continue;

    if (ids.has(c.id)) out.push(c);
  }

  return out;
});



// ─────────────────────────────────────────────
// Comment "See more" truncation (comment-only)
// ─────────────────────────────────────────────
const COMMENT_PREVIEW_MAX_LINES = 2;
const COMMENT_PREVIEW_MAX_CHARS = 130;

// Track expanded state per comment id
const expandedComments = ref(new Set());

const isCommentExpanded = (commentId) => {
  return expandedComments.value.has(commentId);
};

const toggleCommentExpanded = (commentId) => {
  const next = new Set(expandedComments.value);
  if (next.has(commentId)) next.delete(commentId);
  else next.add(commentId);
  expandedComments.value = next;
};

const normalizeCommentBody = (body) => {
  // keep formatting but normalize line endings
  return String(body || '').replace(/\r\n/g, '\n');
};

const isCommentLong = (c) => {
  const body = normalizeCommentBody(c?.body);
  if (!body) return false;

  const lines = body.split('\n');
  if (lines.length > COMMENT_PREVIEW_MAX_LINES) return true;

  return body.length > COMMENT_PREVIEW_MAX_CHARS;
};

const buildCommentPreview = (body) => {
  const src = normalizeCommentBody(body);
  if (!src) return '';

  const lines = src.split('\n');

  let out = '';
  let linesUsed = 0;

  for (let i = 0; i < lines.length && linesUsed < COMMENT_PREVIEW_MAX_LINES; i++) {
    const line = lines[i];
    const prefix = linesUsed === 0 ? '' : '\n';

    const projected = out.length + prefix.length + line.length;

    if (projected > COMMENT_PREVIEW_MAX_CHARS) {
      const remaining = COMMENT_PREVIEW_MAX_CHARS - out.length - prefix.length;
      if (remaining > 0) out += prefix + line.slice(0, remaining);
      linesUsed++;
      break;
    } else {
      out += prefix + line;
      linesUsed++;
    }
  }

  // trim trailing whitespace/newlines so ellipsis doesn't look weird
  out = out.replace(/[\s\n]+$/g, '');

  // Only add ellipsis if we actually truncated
  if (out.length < src.length) return `${out}…`;
  return out;
};

const getCommentDisplayBody = (c) => {
  const body = normalizeCommentBody(c?.body);
  if (!body) return '';

  if (!isCommentLong(c)) return body;
  if (isCommentExpanded(c?.id)) return body;

  return buildCommentPreview(body);
};

const editTarget = ref(null); // { id, type, parentCommentId? }
const editDraftBody = ref('');
const editOriginalBody = ref('');
const editErrorMessage = ref('');
const isSubmittingEdit = ref(false);
const activeEditTextareaEl = ref(null);
const pendingInlineEditMedia = ref(null); // { file, type, previewUrl }
const inlineEditMediaRemoved = ref(false);
const inlineEditMediaInputEl = ref(null);

const setActiveEditTextareaRef = (el) => {
  if (!el) return;
  activeEditTextareaEl.value = el;
};

const autoResizeInlineTextarea = (event) => {
  const el = event?.target;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const isEditingItem = (type, id) => {
  if (!editTarget.value) return false;
  return (
    String(editTarget.value.type) === String(type) &&
    Number(editTarget.value.id) === Number(id)
  );
};

const getInlineEditTargetEntity = () => {
  if (!editTarget.value) return null;
  const targetId = Number(editTarget.value.id);
  const targetType = String(editTarget.value.type || '');
  if (!targetId) return null;

  if (targetType === 'comment') {
    return getCommentById(targetId);
  }
  if (targetType === 'reply') {
    const parentCommentId = Number(editTarget.value.parentCommentId);
    if (!parentCommentId) return null;
    return latestReplyByParentId.value?.[parentCommentId] || null;
  }
  return null;
};

const inlineEditExistingMediaItems = computed(() => {
  const entity = getInlineEditTargetEntity();
  return entity ? getCommentMediaItems(entity) : [];
});

const inlineEditMediaPreviewItem = computed(() => {
  if (pendingInlineEditMedia.value?.previewUrl) {
    return {
      type: pendingInlineEditMedia.value.type,
      src: pendingInlineEditMedia.value.previewUrl,
    };
  }
  if (inlineEditMediaRemoved.value) return null;
  return inlineEditExistingMediaItems.value[0] || null;
});

const hasInlineEditMediaChanges = computed(() => {
  return !!pendingInlineEditMedia.value?.file || inlineEditMediaRemoved.value;
});

const canSubmitInlineEdit = computed(() => {
  return (
    String(editDraftBody.value || '').trim().length > 0 ||
    hasInlineEditMediaChanges.value
  );
});

const setInlineEditMediaInputRef = (el) => {
  inlineEditMediaInputEl.value = el || null;
};

const openInlineEditMediaPicker = () => {
  if (isSubmittingEdit.value) return;
  inlineEditMediaInputEl.value?.click();
};

const clearPendingInlineEditMedia = () => {
  const pending = pendingInlineEditMedia.value;
  if (pending?.previewUrl && pending.previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(pending.previewUrl);
  }
  pendingInlineEditMedia.value = null;
};

const handleInlineEditMediaPicked = (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
    event.target.value = '';
    return;
  }

  clearPendingInlineEditMedia();
  pendingInlineEditMedia.value = {
    file,
    type: file.type.startsWith('video/') ? 'video' : 'image',
    previewUrl: URL.createObjectURL(file),
  };
  inlineEditMediaRemoved.value = false;
  event.target.value = '';
};

const clearInlineEditMediaSelection = () => {
  if (pendingInlineEditMedia.value?.file) {
    clearPendingInlineEditMedia();
    return;
  }
  if (inlineEditExistingMediaItems.value.length) {
    inlineEditMediaRemoved.value = true;
  }
};

const undoInlineEditMediaRemoval = () => {
  inlineEditMediaRemoved.value = false;
};

const focusActiveEditTextarea = async () => {
  await nextTick();
  const el = activeEditTextareaEl.value;
  if (!el) return;
  el.focus();
  try {
    const end = (el.value || '').length;
    el.setSelectionRange(end, end);
  } catch (err) {
    // no-op
  }
  autoResizeInlineTextarea({ target: el });
};

const clearInlineEditState = () => {
  clearPendingInlineEditMedia();
  inlineEditMediaRemoved.value = false;
  if (inlineEditMediaInputEl.value) inlineEditMediaInputEl.value.value = '';
  editTarget.value = null;
  editDraftBody.value = '';
  editOriginalBody.value = '';
  editErrorMessage.value = '';
  activeEditTextareaEl.value = null;
};

const cancelInlineEdit = () => {
  if (isSubmittingEdit.value) return;
  clearInlineEditState();
};

const onEditTextareaKeydown = (e) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    cancelInlineEdit();
    return;
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    submitInlineEdit();
  }
};

const findParentCommentIdForLatestReply = (replyId) => {
  const map = latestReplyByParentId.value || {};
  const target = Number(replyId);

  for (const key of Object.keys(map)) {
    const parentCommentId = Number(key);
    const reply = map[key];
    if (Number(reply?.id) === target) return parentCommentId;
  }
  return null;
};

const handleCommentReplyMenuEdit = ({ id, type }) => {
  const targetId = Number(id);
  if (!targetId) return;

  clearPendingInlineEditMedia();
  inlineEditMediaRemoved.value = false;
  editErrorMessage.value = '';

  if (type === 'comment') {
    const c = getCommentById(targetId);
    if (!c) return;
    const body = String(c.body || '');
    editTarget.value = { id: targetId, type: 'comment', parentCommentId: null };
    editDraftBody.value = body;
    editOriginalBody.value = body;
    focusActiveEditTextarea();
    return;
  }

  if (type === 'reply') {
    const parentCommentId = findParentCommentIdForLatestReply(targetId);
    if (!parentCommentId) return;
    const reply = latestReplyByParentId.value?.[parentCommentId];
    if (!reply) return;

    const body = String(reply.body || '');
    editTarget.value = { id: targetId, type: 'reply', parentCommentId };
    editDraftBody.value = body;
    editOriginalBody.value = body;
    focusActiveEditTextarea();
  }
};

const patchReplyByParentCommentId = (parentCommentId, patch = {}) => {
  const map = latestReplyByParentId.value || {};
  const existing = map[parentCommentId];
  if (!existing) return;

  latestReplyByParentId.value = {
    ...map,
    [parentCommentId]: {
      ...existing,
      ...patch,
    },
  };
};

const submitInlineEdit = async () => {
  if (isSubmittingEdit.value) return;
  if (!editTarget.value) return;

  const trimmedBody = String(editDraftBody.value || '').trim();
  const original = String(editOriginalBody.value || '').trim();
  const mediaChanged = hasInlineEditMediaChanges.value;
  if (trimmedBody === original && !mediaChanged) {
    clearInlineEditState();
    return;
  }

  isSubmittingEdit.value = true;
  editErrorMessage.value = '';

  const targetId = Number(editTarget.value.id);
  const targetType = String(editTarget.value.type || '');

  try {
    let nextAttachments = null;
    if (pendingInlineEditMedia.value?.file) {
      const uploaded = await uploadCommentMediaFile(pendingInlineEditMedia.value.file);
      if (uploaded === null) {
        editErrorMessage.value = 'Could not upload media. Please try again.';
        return;
      }
      nextAttachments = uploaded;
    } else if (inlineEditMediaRemoved.value) {
      nextAttachments = [];
    }

    const hasBody = trimmedBody.length > 0;
    const hasMediaAfterEdit =
      Array.isArray(nextAttachments)
        ? nextAttachments.length > 0
        : inlineEditExistingMediaItems.value.length > 0;

    if (!hasBody && !hasMediaAfterEdit) {
      editErrorMessage.value = 'Comment cannot be empty.';
      return;
    }

    const requestPayload = { body: trimmedBody };
    if (Array.isArray(nextAttachments)) {
      requestPayload.attachments = nextAttachments;
      requestPayload.attachment = nextAttachments;
      requestPayload.attachment_json = JSON.stringify(nextAttachments);
    }

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(targetId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestPayload),
      }
    );

    let payload = null;
    const text = await resp.text().catch(() => '');
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (err) {
        payload = { message: text };
      }
    }

    if (!resp.ok) {
      editErrorMessage.value =
        payload?.error || payload?.message || 'Failed to save comment. Please try again.';
      return;
    }

    const patchedComment =
      payload?.comment && typeof payload.comment === 'object'
        ? payload.comment
        : null;
    const localPatch = {
      body: trimmedBody,
      ...(Array.isArray(nextAttachments) ? { attachments: nextAttachments } : {}),
    };

    if (targetType === 'comment') {
      patchCommentById(targetId, patchedComment || localPatch);
      if (expandedComments.value.has(targetId)) {
        const next = new Set(expandedComments.value);
        next.delete(targetId);
        expandedComments.value = next;
      }
    } else if (targetType === 'reply') {
      const parentCommentId = Number(editTarget.value.parentCommentId);
      patchReplyByParentCommentId(parentCommentId, patchedComment || localPatch);
    }

    scheduleComputeAllThreadLineBottoms();
    clearInlineEditState();
  } catch (err) {
    editErrorMessage.value = 'Network error while saving. Please retry.';
  } finally {
    isSubmittingEdit.value = false;
  }
};

const handleCommentReplyMenuDelete = ({ id, type }) => {
  const targetId = Number(id);
  if (!targetId) return;

  if (isEditingItem(type, targetId)) {
    clearInlineEditState();
  }

  if (type === 'comment') {
    const existing = Array.isArray(comments.value) ? comments.value : [];
    const nextComments = existing.filter((c) => Number(c?.id) !== targetId);
    const removed = nextComments.length !== existing.length;
    comments.value = nextComments;

    if (removed) {
      localCommentsCount.value = Math.max(0, Number(localCommentsCount.value || 0) - 1);
      const nextAdded = new Set(localAddedCommentIds.value || []);
      nextAdded.delete(targetId);
      localAddedCommentIds.value = nextAdded;
    }

    const nextLatest = { ...(latestReplyByParentId.value || {}) };
    delete nextLatest[targetId];
    latestReplyByParentId.value = nextLatest;

    if (Number(openReplyForCommentId.value) === targetId) {
      openReplyForCommentId.value = null;
    }

    commentRowElById.delete(targetId);
    replyAvatarElById.delete(targetId);
    replyComposerAvatarElById.delete(targetId);
    return;
  }

  if (type === 'reply') {
    const parentCommentId = findParentCommentIdForLatestReply(targetId);
    if (!parentCommentId) return;

    const nextLatest = { ...(latestReplyByParentId.value || {}) };
    delete nextLatest[parentCommentId];
    latestReplyByParentId.value = nextLatest;

    const parent = getCommentById(parentCommentId);
    const nextCount = Math.max(0, Number(parent?.replyCount ?? 0) - 1);
    patchCommentById(parentCommentId, { replyCount: nextCount });

    replyAvatarElById.delete(parentCommentId);
  }

  scheduleComputeAllThreadLineBottoms();
};


const openReplyForCommentId = ref(null);
const replyTextByCommentId = ref({}); // { [commentId]: "text" }
const isSubmittingReply = ref(false);
const openReplyEmojiForCommentId = ref(null);
const pendingReplyMediaByCommentId = ref({});
const replyMediaInputRefs = new Map();

const hasReplyContent = (commentId) =>
  String(replyTextByCommentId.value?.[commentId] || '').trim().length > 0 ||
  !!pendingReplyMediaByCommentId.value?.[commentId]?.file;

const hasCommentContent = () =>
  String(commentText.value || '').trim().length > 0 ||
  !!pendingCommentMedia.value?.file;

const setReplyMediaInputRef = (commentId, el) => {
  const id = Number(commentId);
  if (!id) return;
  if (el) replyMediaInputRefs.set(id, el);
  else replyMediaInputRefs.delete(id);
};

const clearPendingReplyMedia = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  const current = pendingReplyMediaByCommentId.value?.[id];
  if (current?.previewUrl && String(current.previewUrl).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(current.previewUrl);
    } catch {
      // no-op
    }
  }
  const next = { ...(pendingReplyMediaByCommentId.value || {}) };
  delete next[id];
  pendingReplyMediaByCommentId.value = next;
  const input = replyMediaInputRefs.get(id);
  if (input) input.value = '';
};

const triggerReplyMediaPicker = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  openReplyEmojiForCommentId.value = null;
  const input = replyMediaInputRefs.get(id);
  input?.click?.();
};

const toggleReplyEmojiPicker = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  openReplyEmojiForCommentId.value =
    Number(openReplyEmojiForCommentId.value) === id ? null : id;
};

const addReplyEmoji = (commentId, emoji) => {
  const id = Number(commentId);
  if (!id) return;
  const value = String(emoji?.i || emoji?.emoji || '');
  if (!value) return;
  replyTextByCommentId.value = {
    ...(replyTextByCommentId.value || {}),
    [id]: `${replyTextByCommentId.value?.[id] || ''}${value}`,
  };
  openReplyEmojiForCommentId.value = null;
};

const handleReplyMediaPicked = (commentId, event) => {
  const id = Number(commentId);
  if (!id) return;
  const file = event?.target?.files?.[0];
  if (!file) return;

  const mime = String(file.type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  if (!isImage && !isVideo) {
    if (typeof notify === 'function') {
      notify({
        title: 'Unsupported file',
        body: 'Please choose an image or video.',
        variant: 'error',
      });
    }
    const input = replyMediaInputRefs.get(id);
    if (input) input.value = '';
    return;
  }

  clearPendingReplyMedia(id);
  pendingReplyMediaByCommentId.value = {
    ...(pendingReplyMediaByCommentId.value || {}),
    [id]: {
      file,
      type: isVideo ? 'video' : 'image',
      previewUrl: URL.createObjectURL(file),
    },
  };
};

const openReplyBox = (commentId) => {
  openReplyForCommentId.value = commentId;

  if (!replyTextByCommentId.value[commentId]) {
    replyTextByCommentId.value = {
      ...replyTextByCommentId.value,
      [commentId]: '',
    };
  }

  scheduleComputeAllThreadLineBottoms();
};


async function submitReply(parentCommentId) {
  const text = String(replyTextByCommentId.value?.[parentCommentId] || '').trim();
  const pendingMedia = pendingReplyMediaByCommentId.value?.[parentCommentId] || null;
  if (!text && !pendingMedia?.file) return;

  if (isSubmittingReply.value) return;
  isSubmittingReply.value = true;

  try {
    let replyAttachments = [];
    if (pendingMedia?.file) {
      const uploaded = await uploadCommentMediaFile(pendingMedia.file);
      if (uploaded === null) throw new Error('upload_failed');
      replyAttachments = uploaded;
    }

    const payload = { body: text };
    if (replyAttachments.length) {
      payload.attachments = replyAttachments;
      payload.attachment = replyAttachments;
      payload.attachment_json = JSON.stringify(replyAttachments);
    }

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(parentCommentId)}/replies`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      }
    );

    if (!resp.ok) {
      const errText = await resp.text().catch(() => '');
      console.error('[NewsfeedCreatorPost] submitReply NOT OK:', resp.status, errText);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const reply = data?.reply || null;
    if (reply && replyAttachments.length && !Array.isArray(reply.attachments)) {
      reply.attachments = replyAttachments;
    }

    console.log('[NewsfeedCreatorPost] ✅ reply created:', reply);

    if (reply) {
  // 1) increment replyCount locally so "View X replies" appears immediately
  const current = getCommentById(parentCommentId);
  const nextCount = (Number(current?.replyCount ?? 0) || 0) + 1;
  patchCommentById(parentCommentId, { replyCount: nextCount });

  // 2) store ONLY the latest reply for local preview (won’t persist after refresh)
  latestReplyByParentId.value = {
    ...latestReplyByParentId.value,
    [parentCommentId]: reply,
  };
}


    // MVP: clear + close box (we are NOT rendering threads on post card yet)
    replyTextByCommentId.value = {
      ...replyTextByCommentId.value,
      [parentCommentId]: '',
    };
    clearPendingReplyMedia(parentCommentId);
    openReplyEmojiForCommentId.value = null;
    openReplyForCommentId.value = null;

    // Later: you can store reply into a replies map + show “View X replies”
  } catch (err) {
    console.error('[NewsfeedCreatorPost] submitReply error:', err);
  } finally {
    isSubmittingReply.value = false;
  }
}


const latestReplyByParentId = ref({}); // { [commentId]: replyObj }

const getCommentReplyCount = (c) => Number(c?.replyCount ?? 0) || 0;

const patchCommentById = (commentId, patch) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  const existing = list[idx] || {};
  list[idx] = { ...existing, ...patch };
  comments.value = list;
};

// --- thread line dynamic bottom alignment (for local reply preview)
// IMPORTANT: DOM refs MUST NOT be stored in reactive refs (causes recursive updates).
const commentRowElById = new Map();   // commentId -> HTMLElement
const replyAvatarElById = new Map();  // commentId -> HTMLElement
const replyComposerAvatarElById = new Map();// commentId -> HTMLElement (REPLY BOX)

const threadBottomPxById = ref({});  // commentId -> number (this CAN be reactive)

const setCommentRowRef = (commentId, el) => {
  if (!commentId) return;
  if (el) commentRowElById.set(commentId, el);
  else commentRowElById.delete(commentId);
};

const setReplyAvatarRef = (commentId, el) => {
  if (!commentId) return;
  if (el) replyAvatarElById.set(commentId, el);
  else replyAvatarElById.delete(commentId);
};

const setReplyComposerAvatarRef = (commentId, el) => {
  if (!commentId) return;
  if (el) replyComposerAvatarElById.set(commentId, el);
  else replyComposerAvatarElById.delete(commentId);
};


// RAF throttle to avoid measurement spam
let threadMeasureRaf = null;

const scheduleComputeAllThreadLineBottoms = () => {
  if (threadMeasureRaf) return;
  threadMeasureRaf = requestAnimationFrame(async () => {
    threadMeasureRaf = null;
    await nextTick();

    const list = Array.isArray(visibleComments.value) ? visibleComments.value : [];
    if (!list.length) return;

    const next = { ...(threadBottomPxById.value || {}) };
    let changed = false;

    for (const c of list) {
      const commentId = c?.id;
      if (!commentId) continue;

      const rowEl = commentRowElById.get(commentId);
      const replyEl =
  replyAvatarElById.get(commentId) ||           // ✅ prefer latest reply preview anchor
  replyComposerAvatarElById.get(commentId);     // ✅ fallback to composer anchor

if (!rowEl || !replyEl) {
  if (typeof next[commentId] !== 'undefined') {
    delete next[commentId];
    changed = true;
  }
  continue;
}


      const rowRect = rowEl.getBoundingClientRect();
      const replyRect = replyEl.getBoundingClientRect();

      const replyCenterY = replyRect.top + replyRect.height / 2;

      let bottom = rowRect.bottom - replyCenterY;
      bottom = bottom + 2; // tiny tune
      bottom = Math.max(0, Math.min(bottom, rowRect.height));

      const rounded = Math.round(bottom);

      if (next[commentId] !== rounded) {
        next[commentId] = rounded;
        changed = true;
      }
    }

    if (changed) {
      threadBottomPxById.value = next;
    }
  });
};

const threadLineBottomStyle = (commentId) => {
  const px = threadBottomPxById.value?.[commentId];
  return typeof px === 'number' ? `${px}px` : '18px'; // fallback to your original
};

// Watch stable keys ONLY (no deep watches)
watch(
  () => (Array.isArray(visibleComments.value) ? visibleComments.value.map((c) => c?.id).join(',') : ''),
  () => scheduleComputeAllThreadLineBottoms()
);

watch(
  () => Object.keys(latestReplyByParentId.value || {}).join(','),
  () => scheduleComputeAllThreadLineBottoms()
);

onMounted(() => {
  scheduleComputeAllThreadLineBottoms();
  window.addEventListener('resize', scheduleComputeAllThreadLineBottoms);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', scheduleComputeAllThreadLineBottoms);
  if (threadMeasureRaf) cancelAnimationFrame(threadMeasureRaf);
});




onMounted(() => {
  window.addEventListener('click', handleGlobalClickToCloseCommentPicker);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', handleGlobalClickToCloseCommentPicker);
   if (commentReactionOpenTimer) clearTimeout(commentReactionOpenTimer);
  if (commentReactionCloseTimer) clearTimeout(commentReactionCloseTimer);
});


const localCommentsCount = ref(Number(props.post?.commentsCount) || 0);
watch(() => props.post?.commentsCount, (v) => {
  localCommentsCount.value = Number(v) || 0;
});



/* ─────────────────────────────
 * Keep in sync if parent refreshes posts
 * ─────────────────────────────*/

 watch(
  () => openReplyForCommentId.value,
  () => scheduleComputeAllThreadLineBottoms()
);


 watch(isCommentActive, (val) => {
  if (val && !commentsLoaded.value) {
    loadCommentsForPost();
  }
});


watch(
  () => props.post?.reactionsSummary,
  (val) => {
    if (val && typeof val === 'object') {
      localReactionsSummary.value = { ...val };
      localReactionsCount.value = Number(val.total) || 0;
    }
  }
);

watch(
  () => props.post?.reactionNamesByType,
  (val) => {
    if (val && typeof val === 'object') {
      localReactionNamesByType.value = JSON.parse(JSON.stringify(val));
    } else {
      localReactionNamesByType.value = { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };
    }
  }
);

watch(
  () => props.post?.reactionsCount,
  (val) => {
    // If someone still updates legacy count only, reflect it
    if (typeof val === 'number' && (!props.post?.reactionsSummary || typeof props.post.reactionsSummary !== 'object')) {
      localReactionsCount.value = val;
      localReactionsSummary.value = {
        ...localReactionsSummary.value,
        total: val,
      };
    }
  }
);

onMounted(() => {
  // Only preload if backend says there are comments
  if ((props.post?.commentsCount || 0) > 0 && !commentsLoaded.value) {
    loadCommentsForPost();
  }
  setupPostViewportTracking();
  unsubscribeCommentVideoReady = websocketStore.subscribe(
    'newsfeed:commentVideoReady',
    onCommentVideoReady
  );
  unsubscribeCommentCreated = websocketStore.subscribe(
    'newsfeed:commentCreated',
    onCommentCreated
  );
  unsubscribeCommentUpdated = websocketStore.subscribe(
    'newsfeed:commentUpdated',
    onCommentUpdated
  );
  unsubscribeCommentDeleted = websocketStore.subscribe(
    'newsfeed:commentDeleted',
    onCommentDeleted
  );
  unsubscribeCommentReactionUpdated = websocketStore.subscribe(
    'newsfeed:commentReactionUpdated',
    onCommentReactionUpdated
  );
});


onBeforeUnmount(() => {
  if (reactionOpenTimer) clearTimeout(reactionOpenTimer);
  if (reactionCloseTimer) clearTimeout(reactionCloseTimer);
  clearPostVisibleTimer();
  clearPendingCommentMedia();
  Object.keys(pendingReplyMediaByCommentId.value || {}).forEach((id) => clearPendingReplyMedia(id));
  if (postViewportObserver) {
    postViewportObserver.disconnect();
    postViewportObserver = null;
  }
  if (typeof unsubscribeCommentVideoReady === 'function') {
    unsubscribeCommentVideoReady();
    unsubscribeCommentVideoReady = null;
  }
  if (typeof unsubscribeCommentCreated === 'function') {
    unsubscribeCommentCreated();
    unsubscribeCommentCreated = null;
  }
  if (typeof unsubscribeCommentUpdated === 'function') {
    unsubscribeCommentUpdated();
    unsubscribeCommentUpdated = null;
  }
  if (typeof unsubscribeCommentDeleted === 'function') {
    unsubscribeCommentDeleted();
    unsubscribeCommentDeleted = null;
  }
  if (typeof unsubscribeCommentReactionUpdated === 'function') {
    unsubscribeCommentReactionUpdated();
    unsubscribeCommentReactionUpdated = null;
  }
});

onBeforeUnmount(() => {
  for (const timer of commentMediaSkeletonFallbackTimers.values()) clearTimeout(timer);
  commentMediaSkeletonFallbackTimers.clear();
});
</script>


<style scoped>
.nf-comment-fade-in {
  animation: nf-comment-fade-in 220ms ease-out both;
}

@keyframes nf-comment-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.nf-media-shell {
  position: relative;
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  vertical-align: top;
}

.nf-media-shell--loading::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #eef2f7 0%, #e3e8ef 50%, #eef2f7 100%);
  background-size: 220% 100%;
  animation: nf-media-shimmer 1.1s linear infinite;
  z-index: 2;
  pointer-events: none;
}

@keyframes nf-media-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -20% 0; }
}

.nf-media-content-hidden {
  opacity: 0;
}

.nf-comment-video-frame {
  width: 100%;
  min-width: 300px;
  max-width: 520px;
  transition: opacity 220ms ease;
}

.inline-edit-textarea,
.inline-edit-textarea:focus,
.inline-edit-textarea:focus-visible,
.inline-edit-textarea:active {
  outline: none !important;
  box-shadow: none !important;
  border: 0 !important;
  -webkit-appearance: none;
  appearance: none;
}

.inline-edit-textarea::-moz-focus-inner {
  border: 0;
}

/* LIKE icon in action bar (sprite-based) */
.newsfeed-like-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -775px;
}

/* COMMENT icon in action bar (sprite-based) */
.newsfeed-comment-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -525px;
}

/* SEND icon in action bar (sprite-based) */
.newsfeed-send-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -888px;
}

/* COMMENT SEND icon in comment area (sprite-based) */
.newsfeed-comment-send-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons-2.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -350px;
}

.newsfeed-like-sprite--active {
  /* Start from a guess near your blue thumb;
     tweak this Y value until you hit the exact frame */

     display: inline-block;
  width: 22px;
  height: 22px;

  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  background-position: 0 -686px;
}

</style>
